import type { Game } from "./game";
import { sameItem } from "./pile";
import { buyableLots } from "./howmany";
import { itemSprite } from "./sprites";
import { spellGlyph } from "./spellicons";
import { viewGlyph } from "./viewicons";
import { longPress, type PressAt } from "./touch";
import { copyText } from "./clipboard";
import { showPopMenu, type PopMenu } from "./popmenu";
import type { AdminStatus, CounterItem, GroundItem, Item, ItemDef, SkillView, SpellView, Text, Trade, Worn, You } from "./types";
import { layOutBag, type BagSort } from "./bag";
import { fmtCn } from "./bagweight";
import { num } from "./numfmt";
import { RateWatch, roughWait } from "./xprate";
import { TapBurst } from "./bagburst";
import { STANCES, stanceGlyph, stanceMeaning, stanceTitle, stanceWord } from "./stance";
import { AUTO_STANCES, autoTitle, autoWord } from "./autostance";
import { boostText, skillHover } from "./skillboost";
import { keptContext } from "./paper";

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

/** One thing currently true of the player, said in words. */
/**
 * One thing on the status line. `full` is the whole of what it says, and
 * it is never abbreviated: not the word, not the unit, not an initialism.
 * "Hasted 20 seconds", not "Hasted 20s"; "Fighting players", not "PvP".
 * A status is read at a glance by somebody in the middle of something,
 * and an abbreviation is a thing to decode rather than a thing to read —
 * the row once collapsed to CBT, SK300 and PZ and nobody could say what
 * was happening to them. The rule was asked for (2026-09-19: "make a specific
 * note about not abbreviating statuses") and TestNoStatusIsAbbreviated
 * holds it.
 */
/** A rate's time left as its status says it: "1d 4h", "2h 5m", "4m". */
function rateWait(secs: number): string {
  const mins = Math.ceil(secs / 60);
  const d = Math.floor(mins / 1440);
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
}

interface Status {
  full: string;
  title: string;
  cls: "good" | "warn" | "bad" | "admin";
  /** Its drawing over the world, a name in VIEW_GLYPHS (2026-10-01: "show statuses as icons to the right of the info icon"). */
  glyph: string;
  /** For a status with a clock, how long is left, as the figure on its icon. */
  clock?: string;
  /** What a long press or a right-click on its icon does, for the one
   *  status that has something to do: the light, put out (2026-10-17). */
  hold?: () => void;
}

/** An admin's own switches as statuses: see statusList. */
function adminStatuses(a: AdminStatus | undefined): Status[] {
  if (!a) return [];
  const out: Status[] = [];
  const cls = "admin";
  if (a.boss) {
    out.push(a.form
      ? { full: `Boss: ${a.form}`, glyph: "boss", cls, title: `Boss: you are playing ${a.form}, with its look, hitpoints, blow, armor, pace and abilities. Monsters take you for one of their own and everybody else may fight you. Beaten, you stand up yourself again; /deboss to be yourself sooner.` }
      : { full: "Boss", glyph: "boss", cls, title: "Boss: monsters take you for one of their own and leave you be, and everybody else sees a monster they may fight. /boss <monster> to take one's form, /deboss to be yourself." });
  }
  if (a.invisible) out.push({ full: "Invisible", glyph: "invisible", cls, title: "Invisible: other players cannot see you, monsters do not go after you, and you stand in nobody's way. /visible to be seen." });
  if (a.invincible) out.push({ full: "Invincible", glyph: "invincible", cls, title: "Invincible: you can still be hurt, but nothing can take your last hitpoint. /vincible to be mortal." });
  if (a.mana) out.push({ full: "Unlimited mana", glyph: "infinitemana", cls, title: "Unlimited mana: spells cost you nothing, and so teach you no magic. /finitemana to spend mana again." });
  if (a.seeall) out.push({ full: "Seeing all", glyph: "seeall", cls, title: "Seeing all: everything in sight is shown to you, however quiet. /seeall again to see by clank." });
  if (a.fullmap) out.push({ full: "Whole map", glyph: "fullmap", cls, title: "Whole map: the map shows every tile of every level, not only what you have explored. /nomap to go back to what you have explored." });
  if (a.hidden) out.push({ full: "Hidden", glyph: "hidden", cls, title: "Hidden: you are off the highscores and the list of who is online. /show to be put back." });
  if (a.nopz) out.push({ full: "No protection zones", glyph: "nopz", cls, title: "No protection zones: none of them shelter you. /nopz with your name to have them back." });
  return out;
}

/**
 * The console's filters: one switch per kind of line the log is told, in
 * the order they appear along the strip. Every kind the server sends has
 * to have one — a kind no switch claims would be a line that cannot be
 * turned off, and, worse, one that a player who had turned everything else
 * off would take for silence.
 *
 * A switch is named by its kind and nothing else: the word on the button
 * is the same word the Types switch prints at the head of the line, because
 * it is the same string. They used to be groups with names of their own —
 * Fights over what is now `dealt` and `received`, World over `event`, Sums over `debug` — which read
 * well on the strip and was no help at all the moment you turned Type on
 * to find out which switch was eating your lines. There is nothing to keep
 * in step now, since there is only one name.
 *
 * Splitting the old groups is the price, and it is worth paying: the two
 * of them covered six kinds between them, so "System" was one switch for a
 * shopkeeper, a shut door and your own mistakes, which are three different
 * things to want quiet.
 */
const LOG_FILTERS: { kind: string; title: string }[] = [
  { kind: "say", title: "What people say out loud, your own words included" },
  // Private messages (2026-10-17: "support direct/private messaging of
  // players in game"): /tell, /w, /msg and /r, both halves.
  { kind: "private", title: "Private messages: what players send you with /tell, and what you send them" },
  { kind: "noise", title: "The noises things make: creatures sounding off nearby, somebody eating, and whatever else gets a sound" },
  // Two switches where there was one, "combat", until 2026-09-27: "split
  // damage types into "dealt" and "received", so they can be filtered
  // independently. if this means combat is not a useful type, remove it".
  // Every line combat carried was one or the other, so combat went.
  { kind: "dealt", title: "Damage you do: your blows, your spells, and what your gear hits back with" },
  { kind: "received", title: "Damage done to you: blows, fire, poison, and what a blood bond carries" },
  { kind: "loot", title: "What you picked up, bought, sold and dropped" },
  { kind: "level", title: "Levels and skills gained, and what killing something was worth" },
  { kind: "event", title: "Who arrived, who left, and who died" },
  { kind: "answer", title: "What you asked and were told: a look, and where somebody is" },
  { kind: "server", title: "What the world says to you: shops, doors, and how things work" },
  { kind: "blocked", title: "The way is shut: what the world will not let you do" },
  { kind: "error", title: "Mistakes of your own: a command that was not one, a thing you cannot reach" },
  // Spells' own lines since 2026-09-27 ("add a chat log category
  // "spells""): the words said as one goes off, anybody's, and everything a
  // spell tells you — what it did, and why it would not go. They were says
  // and server lines until then.
  { kind: "spells", title: "Spells: the words as they are said, what a spell did, and why it would not go" },
  // Sent whether or not the debug switch is on since 2026-09-27 ("debug
  // mode should show the category of chat messages, but not be required to
  // receive debug messages. that should occur anyway and be filtered with
  // the debug filter"), and so off by default: see MUTED_BY_DEFAULT.
  { kind: "debug", title: "The arithmetic behind each swing" },
];

/** The kinds with a switch of their own, for a line to look itself up in. */
const FILTERED = new Set<string>(LOG_FILTERS.map((f) => f.kind));
/** The most lines the log shows; lines a switch hides don't count. */
const LOG_SHOWN = 200;
/** The most lines it holds, hidden ones too (see trimLog). */
const LOG_HELD = 1000;

/**
 * The kinds that have no switch on purpose, and are shown whatever the
 * strip says. There is one: "critical", which is the server telling
 * everybody it is about to go down (2026-09-24: "on sigint, show a critical
 * message to all players"), and since 2026-10-08 the One Ring's
 * destruction ("it should be shown in red so nobody can miss it"). Every
 * other kind can be turned off because
 * every other kind is something a player may reasonably not want to read;
 * this is the one line whose whole point is that nobody misses it, so a
 * switch for it would be a way to be surprised by the thing it warns of.
 * It is a list rather than a silence so that the test holding every kind
 * to a switch can tell a kind left off on purpose from one forgotten.
 */
const UNFILTERED = ["critical"];
void UNFILTERED;

/** The line of `log` that `target`, something pressed, is in, or null. */
function logLineAt(log: HTMLElement, target: EventTarget | null): HTMLElement | null {
  let el = target instanceof Element ? target : null;
  while (el && el.parentElement !== log) el = el.parentElement;
  return el instanceof HTMLElement ? el : null;
}

/**
 * What Copy message copies: the line as it reads, time and all — "12:34
 * Bob: hello" — with nothing of how it is drawn. The kind at the head of
 * a line is left out, shown or not: it is the Types switch's label for
 * the line rather than anything the line says (see addLine).
 */
export function logLineText(line: HTMLElement): string {
  let s = "";
  for (const n of Array.from(line.childNodes)) {
    if (n instanceof HTMLElement && n.classList.contains("kind")) continue;
    // A list's items, a line each as the server wrote them.
    if (n instanceof HTMLElement && n.classList.contains("loglist")) {
      for (const it of Array.from(n.children)) s += "\n  " + (it.textContent ?? "");
      continue;
    }
    s += n.textContent ?? "";
  }
  return s.trimEnd();
}

/**
 * The items of a list a command answered with, as a table that wraps
 * (2026-10-09: "instead of paging long lists in the chat log, can we
 * return a table with wrapping or something?"): columns as wide as the
 * longest item, as many across as the log has room for, read down each in
 * turn (style.css, #log .loglist), and one column
 * when the items are sentences rather than names (LOG_LIST_WIDEST), so a
 * command's line and what it does read straight on, wrapped under itself.
 */
export function logList(items: string[]): HTMLElement {
  const el = document.createElement("div");
  el.className = "loglist";
  const longest = Math.max(...items.map((s) => s.length));
  if (longest > LOG_LIST_WIDEST) el.classList.add("one");
  else el.style.setProperty("--col", `${longest + 2}ch`);
  for (const s of items) {
    const it = document.createElement("span");
    it.textContent = s;
    el.appendChild(it);
  }
  return el;
}

/** The longest item, in characters, a list puts side by side with others. */
export const LOG_LIST_WIDEST = 40;

/**
 * The kinds that start off switched off, for somebody who has never opened
 * the menu. Asked for on 2026-09-22: "filter noise by default in the
 * message log".
 *
 * Noise is the world sounding off — a rat squeaking four tiles away, a
 * dragon rumbling from twenty-four, somebody eating — and it is the one
 * kind that arrives on a clock rather than because something happened.
 * Everything else the console carries has a cause you can point at: a blow
 * landed, a thing picked up, a level, a word said to you. So noise is the
 * only kind that can fill a box holding a couple of dozen lines while
 * nothing whatever is going on, and in a cave with four things breathing
 * nearby it does: the hit that took a third of your health scrolls off the
 * top under a heap of Squeak. and Roar. before you have read it. A log
 * that loses the lines you are in danger over is worse than one that never
 * mentions the rat.
 *
 * This is a default and not a decision. Only a player with nothing stored
 * gets it — see buildLogFilters — so anybody who has ever moved a switch
 * keeps exactly what they moved, noise included. Muting it once for
 * everybody was the other way to read the request and was dropped: it
 * needs a second thing in storage to remember that it has been done, that
 * second thing can only ever go wrong for somebody who had already chosen,
 * and the people it would help are the same people who can work one switch
 * for themselves.
 *
 * A default that hides a kind and says nothing about it would be a kind
 * the player takes for broken, so what makes this safe to do at all is the
 * count already on the button: a brand-new character's chat line reads
 * "Filters (2)" in the accent colour from the first frame (noise and debug), and the menu
 * behind it lists noise struck through, waiting to be clicked back on. See
 * paintFiltersButton, where that argument is made at length.
 */
//
// The damage sums joined it on 2026-09-27, when they started arriving for
// everybody rather than only for somebody who had switched debug on: they
// are a line or two a swing, which is noise's problem ten times over.
const MUTED_BY_DEFAULT = ["noise", "debug"];

/** A sale waiting on how many: what, from which slot, how many make a lot,
 *  what a lot fetches, and how many lots the bag holds. */
/**
 * How many to buy or sell, asked in the one dialog main.ts owns. Everything
 * is counted in lots — what the counter trades at a time, three rocks to a
 * coin or one potion — and the dialog shows things, which is lots times lot.
 * The same dialog both ways since 2026-09-27: "The sell and buy dialogs
 * should allow selecting useful quantities, like 1, a stack, half of all
 * possible and all."
 */
/** A raw skill count as the debug hover shows it: whole, or to one place. */
export function rawUses(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/** A level as the top of the view says it: the ground, or so many up or down. */
export function levelName(z: number): string {
  return z === 0 ? "Ground level" : z > 0 ? `Level ${z} up` : `Level ${-z} down`;
}

/**
 * What the Sell button says after Sell for so many things: what they fetch
 * and the rate (2026-10-19: "show info about how much the npc pays each,
 * and the total, in the sell button"), so "Sell 3 for 9 gold (3 each)", or
 * for a vendor who takes them by the few, "Sell 6 for 2 gold (1 for every
 * 3)". Only whole lots are counted; with fewer than a lot it is just Sell.
 * The rate is why the line under the title no longer says what the vendor
 * pays when Sell is on offer (2026-10-19: "The Elrond pays 3 gold each. is
 * no longer necessary at the top").
 */
export function sellDetail(things: number, lot: number, price: number): string {
  const sold = Math.floor(things / lot);
  if (sold < 1) return "";
  const rate = lot > 1 ? `${num(price)} for every ${num(lot)}` : `${num(price)} each`;
  return `${num(sold * lot)} for ${num(sold * price)} gold (${rate})`;
}

export interface HowMany {
  /** What the dialog does: the word on its button and in its title. */
  verb: "Sell" | "Buy" | "Drop" | "Store" | "Take";
  /** The heading, when it is not the verb and the thing. */
  title?: string;
  /** A second button beside the first, doing something else with the same
   *  number: Drop beside Sell (2026-09-27: "use the sell dialog for drop
   *  too. when both actions are available, make it so you can select how
   *  many then select drop or sell"). */
  also?: { verb: string; go: (lots: number) => void };
  /** Further buttons after that one, in order: at the depot, Sell and Use
   *  beside Store and Drop (2026-09-28: "right clicking items while in the
   *  depot should use the same context dialog as sell and drop"). */
  more?: { verb: string; heavy?: boolean; detail?: (lots: number) => string; go: (lots: number) => void }[];
  /** What the first button says after its verb for so many, in a slightly
   *  muted colour: Sell says what the slider's number fetches (see
   *  sellDetail). */
  detail?: (lots: number) => string;
  /** The line under the slider, when it is not what the lots cost. */
  says?: (lots: number) => string;
  /** Whether the first button can do anything with this many. */
  can?: (lots: number) => boolean;
  /** Who is on the other side of the counter: Grubb, or Elrond. */
  who: string;
  name: string;
  /** Things to a lot. */
  lot: number;
  /** Gold to a lot. */
  price: number;
  /** The most lots there are to trade: every one the bag holds when
   *  selling, and when buying as many as the purse and the bag both allow. */
  lots: number;
  /** What the trade is, said under the title. */
  what: string;
  /** How many lots the slider starts at, when not the verb's own default. */
  start?: number;
  /** What the thing is, under that: its facts. */
  look?: string;
  /** What a look at it says, on a line of its own under the facts and set
   *  apart from them (2026-10-17: "Show the flavour text on a different
   *  line and in a different style in the item dialog"). */
  flavour?: string;
  /** Does it, for the number of lots the dialog ends on. */
  go: (lots: number) => void;
}

/** A button on the spells panel, with the spell it casts as the server last
 *  described it and the cost written under its picture. */
interface SpellButton {
  b: HTMLButtonElement;
  cost: HTMLSpanElement;
  sp: SpellView;
  /** The ready time the cooling sweep was last started for, or 0. */
  ready: number;
  /** The next tick of an ability's countdown, or 0: see paintAbilityClock. */
  tick?: number;
}

/** The gold coin's id: ItemGold, the first id in the server's item table. */
const GOLD = 1;

/** The gold a shopkeeper can be paid from: the bag's, and the depot's too
 *  while a depot box is in reach, as the server spends it (2026-10-19). */
export function availableGold(you: You | null | undefined): number {
  const goldIn = (items: Item[] | undefined) => (items ?? []).reduce((n, it) => (it && it.id === GOLD ? n + it.count : n), 0);
  return goldIn(you?.inv) + (you?.indepot ? goldIn(you.depot) : 0);
}

/**
 * What a long press offers. It was the bag slot's and still carries its
 * name: the thing's name, what it is, and every way of letting go of it.
 * The panel decides what is on the list, since it is the one that knows
 * what is in the slot and whether the depot floor is underfoot; main.ts
 * draws it, since it owns the dialogs.
 *
 * A spell uses the same shape and the same box on the screen, because a
 * long press on a spell wants what a long press on a slot wants — the
 * words a finger cannot hover for, and the action a finger cannot
 * right-click for. See spellMenu. The box itself is #slotmenu either way,
 * so the name has stayed rather than being renamed out from under the
 * element it draws into.
 *
 * `heavy` marks an action worth a second's thought — letting go of a whole
 * pile — so it can be coloured as one rather than sitting in the row
 * looking like the button above it.
 */
export type SlotMenu = {
  title: string;
  look: string;
  /** A disabled action is shown greyed and cannot be pressed; why says why, on hover. */
  actions: { label: string; heavy?: boolean; disabled?: boolean; why?: string; go: () => void }[];
};

/**
 * The second half of what the trade window says when an accept is taken
 * back. The server clears both accepts whenever either offer changes, so
 * nobody is ever bound to an offer they did not see — and the moment that
 * matters is the one where you had accepted, looked away, and the other
 * side swapped a plate for a shirt. A window that quietly unpressed the
 * button would let that go by; this is said in words, in the window, and
 * stays there until you accept again.
 */
export const ACCEPT_CLEARED = "both accepts were cleared. Read both sides again before you accept.";

/** How many squares an offer column shows at the least, so an empty one still has a shape. */
const OFFER_SQUARES = 8;

/** Seconds as m:ss, for the food timer. */
/**
 * A count of seconds in words, for the status line: "1 second", "20
 * seconds". Never "20s" — see Status.
 */
function seconds(n: number): string {
  return `${n} ${n === 1 ? "second" : "seconds"}`;
}

export function mmss(secs: number): string {
  const m = Math.floor(secs / 60);
  return `${m}:${String(secs - m * 60).padStart(2, "0")}`;
}

/** mmss with the hours pulled out once there are any: a bag of mushrooms is
 *  most of an hour of food, and "147:30" is a number to work out. */
function hmmss(secs: number): string {
  if (secs < 3600) return mmss(secs);
  const h = Math.floor(secs / 3600);
  const rest = secs - h * 3600;
  const m = Math.floor(rest / 60);
  return `${h}:${String(m).padStart(2, "0")}:${String(rest - m * 60).padStart(2, "0")}`;
}

/**
 * What a pickup would take off a tile: the thing itself, and how many other
 * things are lying under it. A named type rather than an inline one purely
 * so that the method returning it reads as a method; nothing else uses it.
 */
interface Underfoot {
  it: GroundItem;
  beneath: number;
}

/** A tile, by where it is. */
interface Spot {
  x: number;
  y: number;
}

/** A tile in reach with something on it to take: where, and its top. */
interface Pile extends Spot {
  top: Underfoot;
}

/** The tiles a pickup reaches without walking, as steps from your own: yours, then the eight round it clockwise from north. */
const IN_REACH: readonly (readonly [number, number])[] = [
  [0, 0], [0, -1], [1, -1], [1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1],
];

/** How far a tile in reach is from yours, for which a stacked icon takes first: yours 0, square on 1, a corner 2. */
function far(t: Spot, me: Spot): number {
  return Math.abs(t.x - me.x) + Math.abs(t.y - me.y);
}

/** What makes two things on the ground the same thing, for sharing an icon: everything but where and how many. */
function sameThing(it: GroundItem): string {
  return JSON.stringify([it.id, it.color, it.kind, it.race, it.show, it.name, it.def]);
}

/** DOM side panels: stats, skills, console log. */
/**
 * A count of tiles, said in words: "1 tile", "3 tiles".
 *
 * It exists because every clank in the game is now a small number, and
 * most of them are exactly one. "heard 1 tiles further off" is the sort
 * of thing nobody notices in review and everybody notices on screen, and
 * there are three places that had to get it right — the bag's tooltip,
 * the outfitter's counter and the stats panel — which is two more than
 * any pluralisation should be written in.
 */
export function tiles(n: number): string {
  return `${n} ${Math.abs(n) === 1 ? "tile" : "tiles"}`;
}

/** Points a minute from ms a point, to one place and without a ".0". */
function perMinute(ms: number): string {
  return String(Math.round(600000 / ms) / 10);
}

/**
 * A regeneration rate as its debug row says it (2026-10-07: "show hp and
 * mana regen rate when debug is on"): "75/min", or "0 (hungry)" while it is
 * not coming back. The ms is the server's, ticks and all, so the row is the
 * rate the bar is really refilling at.
 */
export function regenText(ms: number | undefined, why: string | undefined): string {
  if (!ms) return why ? `0 (${why})` : "0";
  // A why beside a live rate is what is hurrying it: "150/min (pipe)".
  return why ? `${perMinute(ms)}/min (${why})` : `${perMinute(ms)}/min`;
}

/** The hover beside it: the same rate as a point every so many seconds, and
 *  what the level's formula asks for when the tick has rounded it up. */
/**
 * What the Attack, Defence and Clank rows mean, said short, for the head of
 * each row's hover; the server's sum for the number follows it (2026-10-08:
 * "when hovering over clank, attack, and defence, in addition to a short
 * description of how it works, show the calculation for the value"). The
 * same words are the rows' titles in index.html until the first state.
 * Defence's are the ones asked for on 2026-09-24 and 2026-10-07: see
 * TestTheDefenceTooltipSaysWhatItIsMadeOf.
 */
const ATTACK_ABOUT =
  "What one swing of yours takes off, before their armor: rolled evenly from a fifth of the top, rounded up, to the top. One swing in fifty is a critical, from the top to twice it, and one in twenty on another player. The top is the weapon in hand, or your bare hands, times 2 and scaled by your skill with it.";
const DEFENCE_ABOUT =
  "A combination of your active defenses, blocking with a shield, parrying with a weapon, etc. Every blow meets a fresh roll anywhere in this range, evenly, from a fifth of its top to its top, and loses that much before your armor meets the rest, for the first two blows in any two seconds; any more in that time meet your armor alone.";
const CLANK_ABOUT =
  "The noise you make, in tiles: the distance monsters notice you from, past their own reach: a tile for most, more for the keen ones. Other players see you from 1 tile further than it, and not from beyond that. It is your body's own noise and every loud piece in use; the quiet pieces take off only what the quietest of them does, and what is only in the bag is silent.";

/** How far a player sees in a light: the server's viewR, the edge of what
 *  it sends. */
const LIT_SIGHT = 10;

/** A row's hover: what it means, then how yours comes to what it shows. */
function calcTitle(about: string, calc: string | undefined): string {
  return calc ? `${about}\n\nYours: ${calc}.` : about;
}

function regenTitle(what: string, ms: number | undefined, formulaMs: number | undefined, why: string | undefined, terms: string): string {
  let s = ms ? `A point of ${what} every ${(ms / 1000).toFixed(2)} s: ${perMinute(ms)} a minute.` : `No ${what} is coming back${why ? `: ${why}` : ""}.`;
  if (ms && why === "pipe") s += " Your lit pipe is doubling it while you sit.";
  if (ms && formulaMs && formulaMs !== ms) {
    s += ` Your level asks for one every ${(formulaMs / 1000).toFixed(2)} s (${perMinute(formulaMs)} a minute), but the world pays out only on its ticks, so a point lands on the first tick after it is due.`;
  }
  return `${s} ${terms} Potions are not counted: what they pay out is drawn on the bar.`;
}

export class UI {
  private log = $<HTMLDivElement>("log");
  /** The button back to the newest line, and the count on it. */
  private logEnd = $<HTMLButtonElement>("logend");
  private logCount = document.createElement("span");
  /**
   * Whether the log follows new lines: on while it is at its newest line,
   * off from the moment the player scrolls back, and on again only when
   * they scroll to the end themselves or press the button (2026-10-07:
   * "when at the newest messages, always scroll forward with new messages
   * unless the player scrolls back. don't move the log window scroll
   * position unless the player scrolls to the end again").
   *
   * It is kept rather than measured afresh for each line, which is what
   * the log did before, because a measurement cannot tell the player
   * scrolling back from the lines under them growing: a narrower window
   * or another font wraps the log onto more lines and leaves the view
   * short of the end without anybody having touched it, and a log that
   * took that for a scroll back stopped following for good.
   */
  private logFollowing = true;
  /**
   * Where the log was last left, by this class or by the player. A scroll
   * event that finds it somewhere else is the player's (or the browser
   * clamping it), and those are the only scroll events that say anything
   * about what the player wants.
   */
  private logTop = 0;
  /**
   * The line holdLogView last kept in place for a reader scrolled back,
   * and where in the box it is kept, until they scroll again.
   */
  private logHeld: { el: HTMLElement; at: number } | null = null;
  /** Lines shown since the player scrolled back: the count on the button. */
  private logUnread = 0;
  private logFilters = $<HTMLDivElement>("filtersmenuacts");
  private filtersBtn = $<HTMLButtonElement>("filtersbtn");
  private typesBtn = $<HTMLButtonElement>("typesbtn");
  /** The switches currently off. Everything not in here is shown. */
  private muted = new Set<string>();
  /**
   * How many lines of each kind the log is holding, shown or hidden, and
   * the figure beside each switch that says so (2026-10-07: "in the chat
   * filters menu, show how many messages exist in each category, included
   * those that were filtered"). "Exist" is the lines the log holds: a
   * filter only hides a line, so these are exactly what switching a kind
   * back on would show, and when the oldest drop off the top (see
   * trimLog) their kinds' counts go down with them. Kept as lines come and
   * go rather than counted off the log, which is hundreds of lines to walk
   * for every squeak.
   */
  private logCounts = new Map<string, number>();
  /** How many of the log's lines are shown, that is not hidden by a
   *  switch: what LOG_SHOWN caps (see trimLog). */
  private logShown = 0;
  private logCountEls = new Map<string, HTMLSpanElement>();
  /** Whether each line is labelled with its kind. Off unless asked for. */
  private inv = $<HTMLDivElement>("inv");
  /** The reading at the top of the Inventory panel: what the bag weighs, of
   *  what can be lifted. */
  /** What is lying in reach: the row of icons, and the key that says whether it changed. */
  private groundDock = $<HTMLDivElement>("grounddock");
  private lastGroundKey = "@";
  private depotPanel = $<HTMLElement>("depotpanel");
  private depot = $<HTMLDivElement>("depot");
  private lastDepotKey = "@";
  private vendorPanel = $<HTMLElement>("vendorpanel");
  private vendor = $<HTMLDivElement>("vendor");
  /** Where a bag square is dropped to sell it: the goods' side of the shop
   *  window (2026-10-19), which the Vendor panel was until then. */
  private sellDrop = $<HTMLElement>("vendorgoodscol");
  private lastVendorKey = "@";
  /** The Equipment panel's slots (2026-10-06: "Add a panel for equipped items"). */
  private equipGrid = $<HTMLDivElement>("equip");
  private lastEquipKey = "@";
  /** A press on a place on the Equipment panel; main.ts offers what goes there. */
  onEquipSlot: (w: Worn, at: HTMLElement) => void = () => {};
  /** A long press or a right-click on one: what is there, said beside it. */
  onEquipInfo: (w: Worn, at: HTMLElement) => void = () => {};
  /** Put the kind id on in place, the bag's best for 0, or nothing for -1. */
  onEquip: (place: string, id: number, v?: string) => void = () => {};
  /** Buy one lot of an item off Grubb's stock; main.ts sends it. */
  onBuy: (id: number, lots?: number) => void = () => {};
  /** A shift-click on a line of the Vendor panel: what the thing is. */
  onLookVendor: (id: number) => void = () => {};
  /**
   * Whether the chat line is folded away under the log, as it is on a
   * phone until it is asked for (see foldChat in main.ts), and the way to
   * ask: a hold on the log off any line opens it as the finger lifts, and
   * Chat on a line's menu opens it at once.
   */
  chatFolded: () => boolean = () => false;
  onChat: (onRelease: boolean) => void = () => {};
  /** Sell lots of the pile in a bag slot to Grubb; main.ts sends it. */
  onSell: (slot: number, it: Item, lots: number) => void = () => {};
  /** Pin a kind to the top of the bag, or unpin it (2026-10-17); main.ts sends it. */
  onPin: (it: Item, on: boolean) => void = () => {};
  onCastOne: (it: Item) => void = () => {};
  /** Pin a spell to the head of the Spells panel, or unpin it (2026-10-17); main.ts sends it. */
  onPinSpell: (sp: SpellView, on: boolean) => void = () => {};
  /** An admin's Destroy: so many of a pile gone for good (2026-10-08). */
  onDestroy: (slot: number, it: Item, count: number) => void = () => {};
  /** Ask how many to buy or sell; main.ts owns the dialog. */
  onHowMany: (ask: HowMany) => void = () => {};
  /** A click on a depot square, which the depot screen takes as a pick: true when it did, and the square is not taken out. */
  onDepotPick?: (slot: number, it: Item) => boolean;
  /** Set for the moment after an item has been dragged, so the click the
   *  browser fires at the end of the drag does not also use the item. */
  private swallowClick = false;
  /** The last bag square a click used: see bagburst.ts. */
  private tapBurst = new TapBurst();
  private statuses = $<HTMLDivElement>("statusicons");
  /** The status icons on the screen, by glyph (see showStatuses), kept from one update to the
   *  next: a clock ticks every second, and an icon drawn afresh each tick
   *  would be gone from under a finger before a long press on it landed. */
  private statusEls = new Map<string, { el: HTMLButtonElement; clock: HTMLSpanElement; st: Status }>();
  private ping = $<HTMLElement>("ping");
  private skills = $<HTMLDivElement>("skills");
  /** The skills the block was last drawn from, for a hold on a row. */
  /** The spells panel's body, which is the row of buttons itself. */
  private spells = $<HTMLDivElement>("spellsbody");
  private lastSpellKey = "@";
  /** Which spells the panel's buttons were built for, by name and icon:
   *  the buttons are only built again when that changes (see showSpells). */
  private spellShape = "@";
  /** The panel's buttons, one to a spell in the order they were built, each
   *  with the spell it casts as the server last described it. */
  private spellButtons: SpellButton[] = [];
  private logoutBtn = $<HTMLButtonElement>("logoutbtn");
  private stopBtn = $<HTMLButtonElement>("stopbtn");
  private pvpBtn = $<HTMLButtonElement>("pvpbtn");
  private autoBtn = $<HTMLButtonElement>("autobtn");
  private chaseBtn = $<HTMLButtonElement>("chasebtn");
  private rideBtn = $<HTMLButtonElement>("ridebtn");
  /** When the horse will stand again, on the server's clock, or 0; see paintRideWait. */
  private rideReady = 0;
  private rideTimer = 0;
  private rideNo = "";
  private dbgBtn = $<HTMLButtonElement>("dbgbtn");
  private lastInvKey = "";
  /** The bag's squares as last drawn, for the Equipment panel's long press (see pressWorn). */
  private bagSquares: Item[] = [];
  /**
   * How the bag is put in order: by kind, or heaviest first (2026-10-08:
   * "add sort by weight to the inventory context menu"). Switched from the
   * bag icon's menu (see sortBag) and kept in this browser, since it is
   * how this player likes the picture, not anything about the world.
   * Anything but "weight" stored, or no storage at all, is by kind.
   */
  private bagSort: BagSort = (() => {
    try {
      return localStorage.getItem("tinytibia.bagsort") === "weight" ? "weight" : "kind";
    } catch {
      return "kind";
    }
  })();
  private lastYouKey = "";
  /** The XP rate's watching (see xprate.ts), and the text it last wrote,
   *  which changes with the clock as well as with the experience. */
  private xpRate = new RateWatch();
  /** The same for each skill's uses, by key, and the hover each skill row
   *  carries before the time to its next level goes on the end. */
  private skillRates = new Map<string, RateWatch>();
  private skillRows: {
    s: SkillView;
    row: HTMLElement;
    name: HTMLElement;
    level: HTMLElement;
    fill: HTMLElement;
    /** The hover without the time to the next level. */
    base: string;
    /** The time to the next level, worked out at most once a minute. */
    eta: string;
    etaAt: number;
    etaLevel: number;
  }[] = [];
  private lastXPRateKey = "";
  /** The last you drawn, for the XP rate's clock: the server sends a state
   *  only when something in it changed, and a rate runs down standing
   *  still. */
  private xpRateYou?: You;
  private xpRateClock = window.setInterval(() => {
    if (this.xpRateYou) this.showXPRate(this.xpRateYou);
  }, 1000);
  /** The trade window's two columns, the names over them, and the line that says when an accept was taken back. */
  private tradeMine = $<HTMLDivElement>("trademine");
  private tradeTheirs = $<HTMLDivElement>("tradetheirs");
  private tradeMineName = $<HTMLElement>("trademinename");
  private tradeTheirName = $<HTMLElement>("tradetheirname");
  private tradeWarn = $<HTMLElement>("tradewarn");
  private tradeAccept = $<HTMLButtonElement>("tradeaccept");

  constructor(
    private game: Game,
    // The whole item goes to the server rather than its id alone, because
    // an id no longer names one pile: a bag can hold Ann's skull and Bob's,
    // and the owner is what says which of them was clicked.
    private onUse: (slot: number, it: Item) => void,
    // depot says the square is a stored one rather than one in the bag:
    // the two lists are numbered separately, so the slot alone is not
    // enough to say which thing was meant.
    private onLook: (slot: number, it: Item, depot?: boolean) => void,
    // How many to let go of, and nought for the whole of the kind.
    private onDrop: (slot: number, it: Item, count: number) => void,
    // The count is how many to move, and nought is a square's worth:
    // that is what a tap at the depot sends, and the long press sends
    // the one, the half or the lot it offered.
    private onDeposit: (slot: number, it: Item, count: number) => void,
    private onWithdraw: (slot: number, it: Item, count: number) => void,
    private onSlotMenu: (menu: SlotMenu | null) => void,
    private onOffer: (it: Item, count: number) => void,
    // One of the icons in the corner saying what is lying in reach,
    // pressed. It passes the tile and nothing else: what a pickup takes is
    // a tile and the server decides what is on top of it, so the client
    // passing an item here would be the client having an opinion about
    // which thing it was going to get.
    private onTakeGround: (x: number, y: number) => void,
    // A spell button pressed. The whole spell goes out rather than its
    // name, because what happens next depends on two things the server
    // said about it — the words, which are what casting is, and whether
    // it wants a name after them — and neither is anything this panel
    // should be keeping its own list of.
    private onCast: (sp: SpellView) => void,
    // Reply and Look on a line of the log somebody said: the name put in
    // the chat box, and a look at where they stand.
    private onReply: (name: string) => void,
    // Message on a line of the log: "/w Name " in the chat box.
    private onMessage: (name: string) => void,
    private onLookAt: (x: number, y: number) => void,
    // A long press on the light's button or its status icon while it is
    // burning: put it out (2026-10-17).
    private onLuxOut: () => void,
  ) {
    this.buildLogFilters();
    this.buildLogTypes();
    this.buildLogEnd();
    this.buildLogCopy();
  }

  /**
   * The button back to the newest line, and what keeps the log following
   * while it is there (2026-10-07: "add a button to scroll the chat log to
   * the very newest messages").
   *
   * The scroll event is how a scroll back is heard, whatever made it — a
   * wheel, a finger, the scrollbar, the keyboard — and it is the only
   * thing that turns following off. The box changing size (the strip on
   * top dragged, the window turned, the panels pushing the console about)
   * and a font arriving both rewrap the lines, and a log that is following
   * is put back on its end after either of them.
   */
  private buildLogEnd() {
    const b = this.logEnd;
    this.logCount.className = "count";
    this.logCount.setAttribute("aria-hidden", "true");
    b.replaceChildren(viewGlyph("newest"), this.logCount);
    // A press with the mouse leaves the focus where it was, so somebody
    // half way through typing a line can look at the newest and go on
    // typing. The click still comes; a finger never moved the focus.
    b.addEventListener("mousedown", (e) => e.preventDefault());
    b.addEventListener("click", () => this.followLog());
    this.log.addEventListener("scroll", () => this.noteLogScroll(), { passive: true });
    new ResizeObserver(() => this.fitLog()).observe(this.log);
    document.fonts?.addEventListener?.("loadingdone", () => this.fitLog());
    this.paintLogEnd();
  }

  /**
   * Copy message: a line of the log right-clicked, or held with a finger,
   * opens the small menu beside the line (2026-10-07: "when right clicking
   * or long pressing a log message, have "copy message" to copy the entire
   * message to the clipboard"; it was a box in the middle of the screen
   * until 2026-10-08, and asked for on 2026-10-07: "right click/long-press
   * in the chat menu should show a context menu, not a full screen menu").
   * The line lights while its menu is up. One listener
   * on the log rather than one on each of its two hundred lines; `want` is
   * what says which line was pressed, and that a press anywhere else in
   * the log — its padding below the last line — is no gesture at all.
   * longPress is the one in touch.ts, so a finger that moves is a scroll
   * and never a hold, and the tap a released hold can mint is swallowed.
   *
   * Text selected with the mouse before a right-click keeps the browser's
   * own menu, whose Copy copies the selection, as it always has (see
   * "Copying from the log" in the help). It is the selection from before
   * the button went down that counts: a Mac selects the word under a
   * right-click on the way down, and that word is not a selection anybody
   * made. On a touchscreen the log is not selectable at all (style.css),
   * since a held finger there now means this menu.
   */
  private buildLogCopy() {
    let line: HTMLElement | null = null;
    let selectedBefore: boolean | null = null;
    const selected = () => {
      const s = window.getSelection();
      return !!s && !s.isCollapsed && s.toString().trim() !== "" && this.log.contains(s.anchorNode);
    };
    this.log.addEventListener(
      "mousedown",
      (e) => {
        if (e.button === 2 || e.ctrlKey) selectedBefore = selected();
      },
      true,
    );
    longPress(
      this.log,
      (at) => {
        // Off every line with the chat line folded away, as below the last
        // line or in an empty log: the hold is for the chat line itself.
        if (!line) {
          this.onChat(true);
          return;
        }
        const l = line;
        // Under the line, or over it near the foot of the screen, starting
        // where it was pressed.
        const r = l.getBoundingClientRect();
        const x = Math.max(r.left, Math.min(at.clientX, r.right));
        l.classList.add("menued");
        showPopMenu(this.logLineMenu(l), { left: x, right: x, top: r.top, bottom: r.bottom }, { onClose: () => l.classList.remove("menued"), on: l });
      },
      (ev) => {
        line = logLineAt(this.log, ev.target);
        if (ev.type === "contextmenu") {
          const had = selectedBefore ?? selected();
          selectedBefore = null;
          if (had) return false;
          // The word a Mac picked out under the right-click goes again.
          if (line && selected()) window.getSelection()?.removeAllRanges();
        }
        return !!line || (ev.type !== "contextmenu" && this.chatFolded());
      },
    );
    // A hold or a right-click on a skill row writes its hover into the
    // log, as a row of Stats does below (2026-10-26: "also support
    // long-press/right click on skills to output to the message log for
    // consistency"); until then a hold opened a box with it, and only on a
    // row an item moves (2026-10-08). It is on the block rather than on
    // each row so that it holds whatever rows the block has.
    let held: HTMLElement | null = null;
    longPress(
      this.skills,
      () => {
        const title = held?.title;
        if (title) this.addLine(`${title.charAt(0).toUpperCase()}${title.slice(1)}`, "answer");
      },
      (ev) => {
        held = ev.target instanceof Element ? (ev.target.closest(".skill") as HTMLElement | null) : null;
        return !!held;
      },
    );
    // A hold or a right-click on a row of Stats writes its hover into the
    // log, since a finger cannot hover (2026-10-17: "Long pressing on clank
    // should show the tooltip info", then "holding should output into the
    // message log"; every row since 2026-10-26: "Long press on stat field
    // (like attack or defence) in panel should output the tooltip into
    // message log"), the way a press on a status icon does: the row's
    // name, then what it means and how yours comes to it. The rows keep
    // their elements across states, only their titles changing, so a
    // title is read at the press. The meal bar has no name of its own on
    // it, so it carries one in data-name.
    for (const row of Array.from(document.querySelectorAll<HTMLElement>("#statbody > [title]"))) {
      longPress(row, () => {
        const name = row.dataset.name ?? row.querySelector("span")?.textContent ?? row.id;
        this.addLine(`${name}: ${row.title.split("\n\n").filter((l) => l).join(" ")}`, "answer");
      });
    }
  }

  /**
   * The menu for one line of the log: Copy message, and for a line
   * somebody else said, Reply to them and Look at them (2026-10-08: the
   * small menu, "like we're supposed to have for message log
   * interactions"). Reply puts their name in the chat box to carry on
   * from; Look is a shift-click on them, greyed while they are out of
   * sight, since a look is at where somebody stands. A line nobody said —
   * the server's, a blow, a level — has only the copy.
   */
  private logLineMenu(line: HTMLElement): PopMenu {
    const text = logLineText(line);
    const items: PopMenu["items"] = [];
    // A phone's chat line is folded away until a hold on the log asks for
    // it (2026-10-10: "on phone, hide the chat entry line behind a long
    // press on the chat log"), and a hold on a line is this menu, so the
    // way to it is the menu's first choice.
    if (this.chatFolded()) items.push({ label: "Chat", title: "Open the chat line to say something", go: () => this.onChat(false) });
    items.push({ label: "Copy message", go: () => void this.copyLogLine(line, text) });
    const name = line.dataset.from;
    if (name) {
      // A private line is answered privately; a say can be either.
      const privately = line.dataset.filter === "private";
      if (!privately) items.push({ label: `Reply to ${name}`, title: `Put ${name}'s name in the chat box`, go: () => this.onReply(name) });
      items.push({ label: privately ? `Reply to ${name}` : `Message ${name}`, title: `Start a private message to ${name} in the chat box`, go: () => this.onMessage(name) });
      // A private line has no id on it, so its writer is found by name.
      const find = () => this.game.creatures.get(Number(line.dataset.who)) ?? [...this.game.creatures.values()].find((o) => o.kind === "player" && o.name === name && o.id !== this.game.myId);
      const c = find();
      items.push({
        label: `Look at ${name}`,
        title: c ? "What a shift-click says of them" : `${name} is not in sight of you`,
        disabled: !c,
        go: () => {
          const now = find();
          if (now) this.onLookAt(now.x, now.y);
        },
      });
    }
    return { label: "Message", items };
  }

  /**
   * Copy a line's words, and say so on the line itself: it lights for a
   * moment with "Copied" in its corner, or "Could not copy" when the
   * browser refused. The log gets no line of its own for it — a line
   * saying a line was copied is a line nobody wanted copied — and the
   * menu has shut by now, so the line, which was under it, is where the
   * eye already is.
   */
  private async copyLogLine(line: HTMLElement, text: string) {
    const ok = await copyText(text);
    line.classList.remove("copied", "copyfail");
    line.classList.add(ok ? "copied" : "copyfail");
    window.setTimeout(() => line.classList.remove("copied", "copyfail"), 1200);
  }

  /** Whether the log is showing its newest line, give or take a few pixels. */
  private logAtEnd(): boolean {
    return this.log.scrollTop + this.log.clientHeight >= this.log.scrollHeight - 4;
  }

  /**
   * Hear a scroll. One that left the log where this class last put it is
   * this class's own and says nothing; any other is the player's, and
   * whether it left the log at its end is whether it follows from now on.
   * A log with no box (hidden, or not laid out yet) measures nought all
   * round, which reads as at the end, so it is not asked.
   */
  private noteLogScroll() {
    const top = this.log.scrollTop;
    if (top === this.logTop || !this.log.clientHeight) return;
    this.logTop = top;
    this.logHeld = null;
    this.setLogFollowing(this.logAtEnd());
  }

  private setLogFollowing(on: boolean) {
    if (on === this.logFollowing && !(on && this.logUnread)) return;
    if (on) this.logUnread = 0;
    this.logFollowing = on;
    this.paintLogEnd();
  }

  /** Put the log on its newest line, and remember having done it. */
  private toLogEnd() {
    this.log.scrollTop = this.log.scrollHeight;
    this.logTop = this.log.scrollTop;
    this.logHeld = null;
  }

  /**
   * Put a following log on its newest line before the next frame is
   * painted rather than now. Going to the end measures the log, and a
   * measurement after a line has gone in lays the page out again, so a
   * fight's blows doing it a line at a time laid the page out a dozen
   * times a tick and were the costliest thing on a phone after the map
   * (2026-10-23: "The game is getting laggy on phone, optimise it"). The
   * lines of a frame go in, and the log goes to its end once, after the
   * scroll events of that frame have been heard: a reader who scrolled
   * back in it is not following by then, and is left where they are.
   */
  private logEndDue = false;
  private logEndSoon() {
    if (this.logEndDue) return;
    this.logEndDue = true;
    requestAnimationFrame(() => {
      this.logEndDue = false;
      if (this.logFollowing) this.toLogEnd();
    });
  }

  /** The button: back to the newest line, and following from there. */
  private followLog() {
    this.toLogEnd();
    this.setLogFollowing(true);
  }

  /**
   * The log's box changed size or a font came in. Following, it goes back
   * on its end, since the lines may have wrapped longer under it; scrolled
   * back, it stays where it is, unless the browser had to move it, which
   * is heard like any other scroll. And the button moves clear of the
   * scrollbar, which is only measurable once there is a box.
   */
  private fitLog() {
    this.logEnd.parentElement?.style.setProperty("--logbar", `${this.log.offsetWidth - this.log.clientWidth}px`);
    this.noteLogScroll();
    if (this.logFollowing) this.toLogEnd();
  }

  /** Show the button while the log is not following, with what came since. */
  private paintLogEnd() {
    const n = this.logUnread;
    this.logEnd.hidden = this.logFollowing;
    this.logCount.hidden = n === 0;
    this.logCount.textContent = n > 99 ? "99+" : String(n);
    const says = n ? `Jump to the newest messages: ${n} new since you scrolled back` : "Jump to the newest messages";
    this.logEnd.title = says;
    this.logEnd.setAttribute("aria-label", says);
  }

  /**
   * Change something about the log — a line in, the oldest out, a kind
   * shown or hidden, a font — without moving what the player is reading.
   *
   * Following, the log is put on its newest line afterwards. Scrolled
   * back, the lines in view are noted with where they stood in it, and
   * afterwards the first of them still there and still shown is put back
   * exactly where it was: lines going in or out above it, or growing,
   * leave it where the eye is. If every line in view has gone (a kind
   * switched off that was all that was showing, or the oldest lines taken
   * off the top while you read them), the first line after them that is
   * still shown takes the place of the first of them.
   *
   * It is done here rather than left to the browser's scroll anchoring,
   * which not every browser has and which, where it is, moves the view
   * for lines taken off the top on top of anything done here (see #log in
   * style.css, where it is switched off).
   */
  holdLogView(change: () => void) {
    // A scroll back whose scroll event has not been heard yet still
    // counts: a line arriving in the same frame must not undo it. A
    // following log already going to its end this frame has had its
    // scroll events heard by then, so it has nothing to measure now: see
    // logEndSoon.
    if (!(this.logFollowing && this.logEndDue)) this.noteLogScroll();
    if (this.logFollowing) {
      change();
      this.logEndSoon();
      return;
    }
    const top = this.log.getBoundingClientRect().top;
    const height = this.log.clientHeight;
    // Every line shown from the first in view down: those in view to put
    // back, and the rest in case none of those is left.
    const lines: { el: HTMLElement; at: number; inView: boolean }[] = [];
    for (const el of Array.from(this.log.children) as HTMLElement[]) {
      if (el.hidden) continue;
      const r = el.getBoundingClientRect();
      if (r.bottom <= top) continue;
      lines.push({ el, at: r.top - top, inView: r.top < top + height });
    }
    change();
    const keep = lines.find((l) => l.el.isConnected && !l.el.hidden);
    if (keep) {
      // Where it is wanted is where it was when the player left it, not
      // where the last of these put it back to: the browser rounds the
      // scroll to whole pixels, and a fraction lost on each of a hundred
      // lines would walk the view a few pixels up the screen.
      const held = this.logHeld;
      const want = held?.el === keep.el ? held.at : keep.inView ? keep.at : lines[0].at;
      const now = keep.el.getBoundingClientRect().top - this.log.getBoundingClientRect().top;
      this.log.scrollTop += now - want;
      this.logHeld = { el: keep.el, at: want };
    }
    this.logTop = this.log.scrollTop;
    // Whatever went in or out may have left the view on the end after all
    // (a kind switched off that was everything below it), and a log at its
    // end follows.
    if (this.log.clientHeight && this.logAtEnd()) this.setLogFollowing(true);
  }

  /**
   * What the console is showing, one switch per kind of message, and the
   * memory of which were off last time. The lines themselves are never
   * thrown away by a filter, only hidden: turning one back on brings its
   * history back with it rather than starting from the next thing that
   * happens.
   *
   * These were a strip of buttons across the top of the console until they were
   * moved (2026-09-19: "Put the chat log filters in a menu shown by a
   * button right aligned at the end of the chat input field"). Eleven
   * switches over a console 150 pixels tall spent a fifth of the log on
   * something read a few times a session, and on a phone they wrapped onto
   * a second line and spent two fifths of it.
   *
   * What the strip did that a menu cannot is announce itself: a filter you
   * have forgotten about looks exactly like a game that has stopped
   * telling you things. So the button carries the count of what is off and
   * wears the accent while anything is — the fact that something is hidden
   * stays outside the menu, and only which kinds it is has to be asked for.
   */
  private buildLogFilters() {
    try {
      const saved = localStorage.getItem("tinytibia.logfilters");
      // Nothing stored at all is somebody who has never moved a switch,
      // and they are the only people the default is for. Anything stored
      // is a choice, and it is taken exactly as it was written down — the
      // empty list included, since "[]" is what a player who has turned
      // noise back on has saved, and reading that as "no preference" would
      // mute it again on their next reload and go on doing it forever.
      if (saved === null) for (const kind of MUTED_BY_DEFAULT) this.muted.add(kind);
      else for (const key of JSON.parse(saved) as string[]) this.muted.add(key);
    } catch {
      // Storage switched off, or nonsense in it: there is no choice to
      // honour either way, so the default stands. The set is emptied
      // first because a parse that threw part way through a list can have
      // left some of it behind, and half of somebody's old preferences
      // plus the default is a state nobody asked for.
      this.muted.clear();
      for (const kind of MUTED_BY_DEFAULT) this.muted.add(kind);
    }
    this.paintFiltersButton();
    this.logFilters.replaceChildren(
      ...LOG_FILTERS.map((f) => {
        const b = document.createElement("button");
        b.type = "button";
        // The kind itself, capitalised by the stylesheet rather than here:
        // what is written on the button has to be the string the lines
        // carry, or the two can drift apart again.
        b.textContent = f.kind;
        b.title = f.title;
        // How many of its lines the log holds, the hidden ones included,
        // after the name (see logCounts).
        const count = document.createElement("span");
        count.className = "count";
        this.logCountEls.set(f.kind, count);
        this.paintLogCount(f.kind);
        b.appendChild(count);
        const paint = () => b.setAttribute("aria-pressed", String(!this.muted.has(f.kind)));
        paint();
        b.addEventListener("click", () => {
          if (!this.muted.delete(f.kind)) this.muted.add(f.kind);
          paint();
          this.paintFiltersButton();
          this.applyLogFilters();
          try {
            localStorage.setItem("tinytibia.logfilters", JSON.stringify([...this.muted]));
          } catch {
            /* storage unavailable: the choice lasts this session */
          }
        });
        return b;
      }),
    );
  }

  /**
   * The Types switch beside Filters: the kind at the head of every log
   * line, shown or not (2026-10-08: "make showing the type of log messages
   * a toggle next to filters"). It was behind the debug switch from
   * 2026-09-19 ("Don't show the type of chat log messages unless debug is
   * enabled"), and before that a switch on the old filter strip; it is a
   * switch of its own again, on the chat line beside the filters it names.
   *
   * Every line carries its label whether or not it is shown, so switching
   * it on names the log you already have rather than starting a new one —
   * one class on the log rather than a walk over every line. Off unless
   * you have switched it on, and remembered like the filters.
   */
  private buildLogTypes() {
    let on = false;
    try {
      on = localStorage.getItem("tinytibia.logtypes") === "1";
    } catch {
      /* storage unavailable: off */
    }
    const paint = () => {
      this.typesBtn.setAttribute("aria-pressed", String(on));
      this.typesBtn.title = on ? "Hide log message types" : "Show log message types";
      this.holdLogView(() => this.log.classList.toggle("types", on));
    };
    paint();
    this.typesBtn.addEventListener("click", () => {
      on = !on;
      paint();
      try {
        localStorage.setItem("tinytibia.logtypes", on ? "1" : "0");
      } catch {
        /* storage unavailable: the choice lasts this session */
      }
    });
  }

  /**
   * The button at the end of the chat line: what it says about the menu
   * behind it. "Filters" while everything is shown, "Filters (2)" with two
   * kinds switched off, and the accent colour to go with the number, so
   * that a console with something missing from it says so without being
   * opened.
   */
  private paintFiltersButton() {
    const off = this.muted.size;
    this.filtersBtn.textContent = off ? `Filters (${off})` : "Filters";
    this.filtersBtn.classList.toggle("muting", off > 0);
    this.filtersBtn.title = off
      ? `${off} of the console's kinds of message are switched off. Click for the list`
      : "What the console is showing: one switch per kind of message";
  }

  /** A line of `kind` come into the log (+1) or gone off its top (-1),
   *  on the count and beside its switch. */
  private tallyLogLine(kind: string | undefined, by: number) {
    if (!kind) return;
    this.logCounts.set(kind, Math.max(0, (this.logCounts.get(kind) ?? 0) + by));
    this.paintLogCount(kind);
  }

  private paintLogCount(kind: string) {
    const el = this.logCountEls.get(kind);
    if (el) el.textContent = String(this.logCounts.get(kind) ?? 0);
  }

  /** Hide or show every line already in the log, after a switch moved. */
  private applyLogFilters() {
    // Showing a kind again lengthens the log above where you were reading,
    // and hiding one shortens it. A log on its newest line is put back on
    // it, and one scrolled back keeps the line being read where it was
    // (see holdLogView).
    this.holdLogView(() => {
      let shown = 0;
      for (const el of Array.from(this.log.children) as HTMLElement[]) {
        el.hidden = this.muted.has(el.dataset.filter || "");
        if (!el.hidden) shown++;
      }
      this.logShown = shown;
      this.trimLog();
    });
  }

  /**
   * Take the oldest lines off the top until the log shows no more than
   * LOG_SHOWN of them and holds no more than LOG_HELD in all. Only shown
   * lines count towards the first, so a filter never leaves the log
   * shorter than it could be (2026-10-20: "Don't count lines hidden by a
   * filter, so there's always up to 200 lines if there's enough"). Hidden
   * lines are kept, for switching their kind back on, but only so many: a
   * quiet log with a busy kind switched off would otherwise keep every
   * line of it forever.
   *
   * Each switch's count goes down with every line of its kind taken off,
   * hidden or not (see logCounts). Called inside holdLogView, which puts
   * a reader scrolled back where they were.
   */
  private trimLog() {
    while (this.logShown > LOG_SHOWN || this.log.childElementCount > LOG_HELD) {
      const old = this.log.firstElementChild as HTMLElement;
      this.log.removeChild(old);
      if (!old.hidden) this.logShown--;
      this.tallyLogLine(old.dataset.filter, -1);
    }
  }

  /** The items of a list for the next addLine to draw under its words, and
   *  only the next (see onText and logList). */
  private lineList?: string[];

  /**
   * One line in the console. `kind` is the wire kind, which is at once the
   * line's colour, the word the Types switch shows and the switch that
   * hides it. `variant` is an extra class for a line that is a shade of
   * its kind rather than a kind of its own — your own words are a `say`
   * like anybody else's and are filtered with them, and only look
   * different.
   */
  addLine(text: string, kind: string, variant?: string, from?: { name: string; id: number }) {
    const div = document.createElement("div");
    div.className = variant ? `${kind} ${variant}` : kind;
    // Who said it, for Reply and Look on the line's menu (see logLineMenu).
    if (from) {
      div.dataset.from = from.name;
      div.dataset.who = String(from.id);
    }
    // A kind no switch claims is shown whatever the strip says: better an
    // unfilterable line than a silently swallowed one.
    if (FILTERED.has(kind)) {
      div.dataset.filter = kind;
      div.hidden = this.muted.has(kind);
    }
    // Each line opens with the local time it arrived, Tibia-style, so a
    // scroll back through the log says when as well as what.
    const now = new Date();
    const two = (n: number) => String(n).padStart(2, "0");
    const ts = document.createElement("span");
    ts.className = "ts";
    ts.textContent = `${two(now.getHours())}:${two(now.getMinutes())} `;
    div.appendChild(ts);
    // What kind of line this is, in that kind's own colour. It is put on
    // every line and shown only while the Types switch is on, so turning it
    // on names what is already there rather than starting a new log.
    const label = document.createElement("span");
    label.className = `kind k-${kind}`;
    label.textContent = kind;
    div.appendChild(label);
    div.appendChild(document.createTextNode(text));
    if (this.lineList) div.appendChild(logList(this.lineList));
    this.lineList = undefined;
    // The log follows new lines only while it is on its newest one:
    // somebody scrolled back to read is left where they are, rather than
    // yanked to the bottom by every squeak and blow (2026-09-24: "don't
    // automatically scroll the chat log to the bottom when a new message
    // appears"), and stays there until they scroll to the end again or
    // press the button (2026-10-07, see logFollowing).
    //
    // The oldest lines go past two hundred shown (see trimLog). Taking
    // them off the top moves everything under them up by their height,
    // and holdLogView puts the line a reader scrolled back was reading
    // back where it was.
    //
    // Each switch's count goes up with its line (see logCounts).
    this.holdLogView(() => {
      this.log.appendChild(div);
      if (!div.hidden) this.logShown++;
      this.tallyLogLine(div.dataset.filter, 1);
      this.trimLog();
    });
    // What they have not seen yet, on the button. A line a filter hides
    // is not news.
    if (!this.logFollowing && !div.hidden) {
      this.logUnread++;
      this.paintLogEnd();
    }
  }

  onText(t: Text) {
    if (t.kind === "say") {
      // Your own half of the conversation is a say in its own colour, not
      // a kind of its own: it is filtered with everything else anybody
      // says, which is the only way anyone would want it.
      // A spell's words are a say too, and filtered with the says, but
      // in their own colour, yours or anybody's (2026-09-24: "show the
      // words for spells in another color").
      const own = t.id === this.game.myId;
      // Heard from the Seat of Hearing, from beyond where it carries: where
      // it was said goes after the name (2026-10-21).
      const who = t.far ? `${t.from} (${t.far})` : t.from;
      // Somebody else, player or NPC, whose line can be replied to and
      // whose speaker looked at from its menu; your own cannot.
      const from = !own && t.from && t.id ? { name: t.from, id: t.id } : undefined;
      // Under "spells" rather than "say" since 2026-09-27 ("add a chat log
      // category "spells""), still in the spell colour.
      if (t.spell) this.addLine(`${who}: ${t.text}`, "spells", "spell", from);
      // A yell says so, as Tibia's log does: "Name yells: WORDS".
      else this.addLine(t.yell ? `${who} yells: ${t.text}` : `${who}: ${t.text}`, "say", own ? "own" : undefined, from);
    } else if (t.kind === "private") {
      // "To Bert: words" for your own, "Bert: words" for theirs, and the
      // line names the other one either way, so its menu can write back.
      const other = t.to ?? t.from ?? "";
      const words = t.to ? `To ${t.to}: ${t.text}` : `${t.from}: ${t.text}`;
      this.addLine(words, "private", t.to ? "own" : undefined, other ? { name: other, id: 0 } : undefined);
    } else if (t.kind === "noise") {
      // A noise heard from the Seat of Hearing is on the screen, pinned
      // to the edge it came from, and not in the log (2026-10-21: "when on
      // amon lhaw, the noises should appear on screen, not in the message
      // log").
      if (!t.far) this.addLine(`${t.from}: ${t.text}`, "noise");
    } else if (t.list?.length) {
      this.lineList = t.list;
      this.addLine(t.text.split("\n", 1)[0], t.kind);
    } else this.addLine(t.text, t.kind);
  }

  /**
   * The tooltip on an inventory slot: what the item is worth and what a click
   * on it does. The server decides what is in use, since skills weigh in on
   * which weapon hits hardest.
   */
  /**
   * What the thing is, in numbers: the part of a slot's tooltip that is
   * fact rather than advice. It is kept apart from the rest because the
   * touch menu wants exactly this and none of the clicking — the buttons
   * under it are the advice, and "right-click to drop one" read off the
   * top of a menu opened with a finger would be telling somebody to do
   * the one thing they cannot.
   */
  private slotFacts(def: ItemDef, it?: Item): string[] {
    // Short, and facts only (asked for on 2026-09-24: "make shield tooltip
    // more concise: remove the 'doubled into the pool' thing, just put
    // 'clank: +1' if it makes noise, don't mention the controls for how to
    // drop etc. do this concision across other types of items too"). How
    // the numbers go into a fight is the help's to explain; the tooltip is
    // what the thing is.
    //
    // What the kind is comes from the server, worded once (2026-10-08:
    // "looking at an item should show its stats, i'm only seeing that on
    // tooltip"): a look at the thing says the same words, so the two
    // cannot fall out of step. See ItemDef.facts in itemfacts.go.
    const parts: string[] = [...(def.facts ?? [])];
    // In the time it lasts whoever is reading: half as long again for a
    // hobbit (2026-09-29).
    if (def.food) parts.push(`food ${mmss(Math.round(def.food * (this.game.you?.foodmul ?? 1)))}`);
    // Whose head it was: two slots of skulls are told apart by this alone.
    if (it?.owner) parts.push(`${it.owner}'s`);
    if (it && it.count > 1) parts.push(`×${num(it.count)}`);
    // What it weighs, in coins: one number for one of them, and the pile's
    // own when there is a pile, since what a bag is carrying is the thing
    // the reading at the top of the panel is counting. It said how many
    // went in a square from 2026-09-23 until the weight came back on
    // 2026-10-04 ("reintroduce encumbrance").
    if (def.cn) parts.push(it && it.count > 1 ? `${fmtCn(def.cn)} cn each, ${fmtCn(def.cn * it.count)} cn in all` : `${fmtCn(def.cn)} cn`);
    return [def.name, ...parts];
  }

  /**
   * A kind's numbers on one line, without its name: what the Equipment
   * panel's menu writes under each choice's name. Nothing for a kind the
   * client was never told about.
   */
  itemFacts(id: number, made?: ItemDef): string {
    const def = made ?? this.game.itemDefs.get(id);
    return def ? this.slotFacts(def).slice(1).join(" · ") : "";
  }

  private slotTitle(def: ItemDef, inUse: boolean, twoHanded: boolean, it?: Item, _chosen = false): string {
    const parts = this.slotFacts(def, it);
    // What state the piece is in, and nothing about which button does what.
    if (inUse) parts.push("in use");
    else if ((def.shield || def.offhand) && twoHanded) parts.push("down: both hands are full");
    return parts.join(" · ");
  }

  /** The skills block: a row per skill, its name and level over its progress. */
  private showSkills(skills: SkillView[], debug = false) {
    // The rows are made once for a set of skills and filled in place after
    // that, so the row under the pointer stays the same element while its
    // numbers move (2026-10-26: "the tooltip for training progress isn't
    // stable and keeps changing"). This is called whenever any stat
    // changes, mana coming back included, and a row made afresh each time
    // took its tooltip away with it, to come back saying something else.
    const same = skills.length === this.skillRows.length && skills.every((s, i) => this.skillRows[i].s.key === s.key);
    if (!same) {
      this.skillRows = skills.map((s) => {
        const row = document.createElement("div");
        row.className = "skill";
        const name = document.createElement("span");
        const level = document.createElement("b");
        const bar = document.createElement("div");
        bar.className = "skillbar";
        const fill = document.createElement("div");
        bar.appendChild(fill);
        // The bar first, so it lies behind the words (see style.css).
        row.append(bar, name, level);
        return { s, row, name, level, fill, base: "", eta: "", etaAt: -Infinity, etaLevel: s.level };
      });
      this.skills.replaceChildren(...this.skillRows.map((r) => r.row));
    }
    skills.forEach((s, i) => {
      const r = this.skillRows[i];
      r.s = s;
      // Two things and nothing else: what the level reached does, in the
      // server's words, and how far it is to the next one (asked for on
      // 2026-09-27: "in the skill tooltips, mention only progress to the
      // next level, and the effect of the current level (like 220%
      // attack, or a chance to catch a fish or whatever)"). It used to
      // price the level in hours and say what a death takes, which is the
      // help's to say once rather than every skill's to repeat.
      //
      // And, for a skill the items in use move, what each one adds and
      // what the skill comes to with them (2026-10-08: "when skills are
      // modified by items, show the adjustment in the skills panel").
      r.base = skillHover(s);
      // With the debug switch on, the raw count under it as well (asked
      // for on 2026-09-27: "when debug is enabled, show the raw value in
      // skill tooltips"), to one decimal since magic and the pace of a
      // high level both hand in fractions.
      if (debug) r.base += ` Raw: ${rawUses(s.uses)} of ${rawUses(s.next)} uses.`;
      const title = r.base + r.eta;
      if (r.row.title !== title) r.row.title = title;
      if (r.name.textContent !== s.name) r.name.textContent = s.name;
      // What the items add, after the level earned and in a colour of
      // its own, so "12 +5" reads as twelve learned and five lent: the
      // bar behind is still the way to 13.
      const boost = boostText(s.boost);
      const levelKey = `${s.level}${boost}`;
      if (r.level.dataset.shows !== levelKey) {
        r.level.dataset.shows = levelKey;
        r.level.textContent = String(s.level);
        if (boost) {
          const adj = document.createElement("i");
          adj.className = (s.boost ?? 0) < 0 ? "boost less" : "boost";
          adj.textContent = ` ${boost}`;
          r.level.append(adj);
        }
        r.row.classList.toggle("boosted", !!boost);
      }
      const width = `${s.pct}%`;
      if (r.fill.style.width !== width) r.fill.style.width = width;
    });
  }

  /**
   * There is no line saying how full the bag is, and this is where it was.
   *
   * It read "14 / 25" above the squares, in the panel's own muted grey,
   * turning gold five squares from the limit and red at it. It went on
   * 2026-09-23: "Don't show the caption that shows how many slots are in
   * use, we can see visually." The squares are drawn in a grid that is as
   * tall as the bag is full, so the number was saying a second time what
   * the panel says by its own shape — and it had been saying it about
   * kinds, which stopped being the rule the day before, so the panel is
   * one thing again rather than a picture with a caption arguing with it.
   *
   * What went with it is the colour, which was the half that did not have
   * to be read: quiet while there was room, the accent from five off, red
   * at the line, so a bag filling up during a fight could be noticed
   * without looking straight at it. A player now finds out that their bag
   * is full when something refuses to go in and the log says so. If that
   * turns out to be too late to be useful, the thing to bring back is the
   * colour rather than the number — the last row of squares could carry
   * it, or the panel's own title.
   */

  /**
   * What is lying in reach, as a row of icons over the corner of the world
   * (2026-10-20: "When standing next to items within pick up range, show
   * the items as icons along the bottom from the right in portrait mode,
   * and from the bottom on the right in landscape mode. Use the highlight
   * technique we have for the depot. Remove the old code for showing an
   * item that we're standing in at extra size"). It was one icon, for the
   * tile under your feet alone, drawn twice the size of the marks at the
   * top (2026-09-22, then 2026-09-24); now it is an icon for every tile a
   * pickup reaches — yours and the eight round it, which is what the
   * server's pickup takes without walking (Chebyshev 1, same level) — at
   * the size of the other icons over the world, lit as the depot's is
   * when you stand at a box (.urgent, see urgenticon.ts), since each is a
   * thing beside you to be had for a press.
   *
   * One icon a top and not one a thing, though a tile holds a pile. A
   * press puts exactly one thing in your bag: the server reaches down the
   * pile from the top, past the marks, and takes the first real thing it
   * finds (see pickup in world.go), and the message names a tile and not
   * an item. So an icon is the top of a pile, and what is under it is
   * said in its hover, and taking it shows the next. Tiles whose tops are
   * the same thing share one icon, counting all of them (2026-10-22), and
   * its press takes the nearest of them (see groundIcon).
   *
   * Your own tile is first, in the corner; then the tiles round you,
   * starting north and going round clockwise. Which way the row runs is
   * the stylesheet's: along the foot from the right edge when the world
   * is taller than it is wide, and up the right edge from the bottom when
   * it is wider.
   *
   * The same walk down the pile as the server's (underfoot below): blood,
   * spatter and bones are marks and reached past, and scenery — a door, a
   * chest, the depot's box, the board on the broken bridge — is not
   * carried, so a tile whose top is scenery has no icon.
   *
   * Pressing one is the pickup a click on that tile already sends, with
   * the same coordinates; the icon is a bigger target for a gesture the
   * game already had, and one that is not under whatever is standing on
   * the tile.
   */
  private showGround() {
    const me = this.game.me();
    const piles: Pile[] = [];
    if (me) {
      for (const [dx, dy] of IN_REACH) {
        const x = me.x + dx, y = me.y + dy;
        const top = this.underfoot(x, y);
        if (top) piles.push({ x, y, top });
      }
    }
    const key = piles.map(({ x, y, top }) => `${x},${y}:${top.it.id}/${top.it.color ?? ""}x${top.it.count}|${top.beneath}`).join(";");
    if (key === this.lastGroundKey) return;
    this.lastGroundKey = key;
    // Tiles whose tops are the same thing share one icon (2026-10-22:
    // "icons for nearby items could stack for the same types. for example
    // a steel shield in one adjacent tile, and 2 steel shields in another
    // could just show 3 steel shields"). The same thing is what the bag
    // would stack: the kind, and whatever makes one of it differ from
    // another (a skull's colour, a made piece's make, a Great Ring's
    // name), so a dwarf-wrought sword does not hide among plain ones.
    const stacks = new Map<string, Pile[]>();
    for (const p of piles) {
      const k = sameThing(p.top.it);
      const s = stacks.get(k);
      if (s) s.push(p);
      else stacks.set(k, [p]);
    }
    this.groundDock.replaceChildren(...[...stacks.values()].map((s) => this.groundIcon(s, me!)));
  }

  /**
   * The icon for one thing in reach, lying on one tile or several: the
   * thing, lit, with how many of it there are on all of them together,
   * and pressed to take it.
   *
   * A press still sends one pickup, of one tile, since that is what the
   * message names: the nearest of them — your own tile, then one square
   * on to you, then a corner, and north-first round each — so the count
   * goes down by that tile's pile and the next press takes the next.
   */
  private groundIcon(tiles: Pile[], me: Spot): HTMLButtonElement {
    const near = [...tiles].sort((a, b) => far(a, me) - far(b, me));
    const { x, y, top } = near[0];
    const { it, beneath } = top;
    const count = tiles.reduce((n, t) => n + t.top.it.count, 0);
    const mine = tiles.every((t) => t.x === me.x && t.y === me.y);
    const def = this.game.defOf(it);
    const name = def?.name ?? "something";
    const b = document.createElement("button");
    b.type = "button";
    b.className = "urgent";
    const c = document.createElement("canvas");
    c.width = 32;
    c.height = 32;
    keptContext(c).drawImage(itemSprite(it.id, it.color, this.game.defOf(it)?.make), 0, 0);
    b.appendChild(c);
    if (count > 1) {
      const n = document.createElement("span");
      n.className = "count";
      n.textContent = num(count);
      b.appendChild(n);
    }
    // What it is, where, that pressing takes it, and — only when there is
    // one — what is lying under the piece a press takes, since that is
    // the one thing the icon cannot show.
    const what = count > 1 ? `${num(count)} ${name}` : name;
    const where = mine ? "at your feet" : tiles.length > 1 ? `on ${tiles.length} tiles beside you` : "beside you";
    const first = tiles.length > 1 ? ` Pressing takes the nearest${it.count > 1 ? `, ${num(it.count)} of them` : ""}, then the next.` : "";
    const under = beneath > 0
      ? ` There ${beneath === 1 ? "is 1 more thing" : `are ${beneath} more things`} under ${tiles.length > 1 ? "the nearest" : "it"}, and taking it uncovers the next.`
      : "";
    b.title = `${what} ${where}. Click to pick it up.${first}${under}`;
    b.setAttribute("aria-label", `Pick up ${what} ${where}`);
    b.onclick = () => this.onTakeGround(x, y);
    // A finger cannot hover, so the hold opens the same box a hold on a
    // bag square or a spell opens, carrying the words the pointer gets and
    // a button that does the thing. The hold must never take the item
    // itself: that is a request for words answered by the floor changing
    // under you. longPress is the one in touch.ts, whose swallow is what
    // keeps the release of the hold off the click above.
    longPress(b, () =>
      this.onSlotMenu({
        title: what,
        look: `It is lying ${where}. Pressing this picks it up.${first}${under}`,
        actions: [{ label: "Pick it up", go: () => this.onTakeGround(x, y) }],
      }),
    );
    return b;
  }

  /**
   * The thing a pickup would take off the tile at (x, y): the top of the
   * pile with the marks reached past, or null when there is nothing there
   * worth bending down for. `beneath` is how many other things are lying
   * under it, which is all the tooltip needs and all a single icon can
   * honestly say about a stack.
   */
  private underfoot(x: number, y: number): Underfoot | null {
    const here = this.game.itemsAt(x, y);
    let i = here.length - 1;
    while (i >= 0 && this.game.itemDefs.get(here[i].id)?.decor) i--;
    if (i < 0) return null;
    if (this.game.itemDefs.get(here[i].id)?.static) return null;
    return { it: here[i], beneath: i };
  }

  /**
   * The spells, one button each, in the Spells panel, in one of three
   * colours — green for one that will go off, the gold accent for one
   * that will go off and do nothing worth the mana, red for one the
   * server would refuse.
   *
   * A button is a picture and a number, as many across as the panel has
   * room for, and no longer a
   * full-width row with the spell's name written on it (asked for on
   * 2026-09-22: "Use the latest revision of the spell svgs for the spell
   * panel. Don't keep the names alongside, put that in the tooltip. On
   * mobile make that appear with a long press"). Twelve names one to a
   * line was a column a third the height of the window and a thing to
   * read down; twelve glyphs in a three-by-four block is a thing to
   * glance at, and the one you want is in the same square it was in
   * yesterday, which a list sorted by cost cannot promise once a level
   * adds a row in the middle of it.
   *
   * That is the palette, and the only layout: a list came back as the
   * other half of a toggle on 2026-09-28 and went with the toggle on
   * 2026-10-01 ("remove the spellmode selector").
   *
   * Which picture goes on which spell is the server's to say and arrives
   * on the wire as sp.icon; what the picture looks like is spellicons.ts.
   * A spell whose key this client has no drawing for keeps its name on
   * the face of the button instead — small, wrapped, still pressable —
   * because a blank square is the one thing worse than a long label.
   *
   * The cost stays on the button. The request moved the names and said
   * nothing about the numbers, and the number is the one that earns its
   * place: the list is sorted cheapest first, so the costs read as a
   * ladder down the panel, and the commonest reason a button is red is
   * that this number is bigger than the pool. Take it off and the red is
   * unexplained until somebody hovers — which on a telephone is a long
   * press and a dialog away. It is small and quiet and sits under the
   * glyph, so it is a caption rather than a second thing competing to be
   * read.
   *
   * The name went into the hover, and the hover now leads with it, since
   * it is no longer anywhere on the screen: the name and the cost, then
   * what the spell does, then the refusal or the reason it is moot. On a
   * touchscreen there is no hovering at all, so a long press opens the
   * same dialog a long press on a bag slot opens, carrying the same three
   * things and a button that casts.
   *
   * Nothing in here decides which is which. The server sends two
   * sentences per spell — why it would refuse, and failing that why it
   * would be pointless — or neither, and this reads those two fields and
   * nothing else. It would be easy and wrong to work it out from the mana
   * beside it: the mana is only the commonest of the reasons, and the
   * others are a fight still running, a skull, a light already lit, an
   * empty room, which are rules living on the other side of a wire and
   * free to grow. A panel that guessed would go green on a spell the
   * server is about to refuse, which is worse than a panel that says
   * nothing.
   *
   * So the hover is the server's words too: the name first, then what the
   * spell does, always, and then the refusal or the reason it is moot
   * after it when there is one. The repo tells people the reason rather
   * than just refusing — a
   * logout button that will not work says why, and for how much longer — and a
   * coloured button with no explanation is exactly the thing that makes
   * a player think the game is broken.
   *
   * And a red button is still a button. It is not disabled: the click goes
   * to the world as the words, the server refuses it out loud in the
   * console, and that refusal is the authoritative one — the colour was
   * only ever a hint, worked out a tick ago, before the rat bit you. A
   * gold one casts too, and the world does what the hover said it would.
   *
   * Spells the pool cannot hold are not sent, so a level one's list is
   * empty, and a level two's has sanctification alone, the one spell
   * five mana buys (on the panel again since 2026-10-12). An
   * empty panel body reads as broken, so it says in a quiet line what it
   * is, and that line is the server's too (you.spellsnote): which spell
   * comes first and at what level, which is a different level for an elf
   * or a dwarf than for a man, and "You are dead" while the body lies
   * where it fell, when every button would be red for the one reason
   * (2026-10-09: "When you can't cast spells, the message contents of the
   * spell panel should be appropriate"). It used to be "Nothing you can
   * hold yet" with a man's arithmetic in a hover a phone cannot reach.
   */
  private showSpells(spells: SpellView[], note?: string) {
    if (note || spells.length === 0) {
      const none = document.createElement("span");
      none.className = "none";
      none.textContent = note || "No spells yet.";
      this.spells.replaceChildren(none);
      this.spellShape = "";
      this.spellButtons = [];
      return;
    }
    // The buttons are built only when the spells themselves change — one
    // learnt at a level, a picture that arrived — and every other change is
    // painted onto the buttons already there. It used to build them all
    // again on every change, and the changes come on their own clock: a
    // refusal counts down a second at a time ("Wait 7 seconds") and a gold
    // button turns green as a monster walks into reach. A click is a press
    // and a release on the same element, so a press that landed on a
    // button and a release that landed on its replacement was no click at
    // all, and the spell did not go off until it was clicked again (asked
    // about on 2026-09-27: "sometimes spells don't go off if i click them,
    // i have to click again, like a weird focus thing or something").
    //
    // The layout is part of the shape (2026-09-28: "add a toggle to the
    // spell panel to show a list (name included) or palette (how it
    // currently looks). default to palette"): a list row carries the name
    // and the words and a palette square does not, so switching builds the
    // buttons again — once, on the press of the toggle, never mid-click.
    //
    // Every spell is on the panel, scrolling along one row under the world
    // as the bag does and down the side of a wide screen, since
    // 2026-10-17 ("Remove the ellipsis from spell panel"). The utility
    // spells, which the server lists last, went behind a "…" button when
    // the panel was short of room until then.
    //
    // A pin is part of the shape (2026-10-17: "Support pinning spells"):
    // the server sorts the pinned spells first, so a pin moves the button,
    // and the button wears the pin.
    const shape = spells.map((sp) => `${sp.name}/${sp.icon ?? ""}${sp.pinned ? "/pinned" : ""}`).join("|");
    if (shape !== this.spellShape) {
      this.spellShape = shape;
      this.spellButtons = spells.map((sp) => this.spellButton(sp));
      this.spells.replaceChildren(...this.spellButtons.map((e) => e.b));
      spells.forEach((sp, i) => this.paintSpell(this.spellButtons[i], sp));
      return;
    }
    spells.forEach((sp, i) => this.paintSpell(this.spellButtons[i], sp));
  }

  /**
   * One spell's button, built once: the picture (or the name, when there is
   * no picture for its key), the cost under it, and the two ways of using
   * it. The listeners read the spell off the entry rather than holding the
   * sp the button was built for, so a button painted with a newer
   * description casts what that description says.
   */
  private spellButton(sp: SpellView): SpellButton {
    const b = document.createElement("button");
    b.type = "button";
    // The layout is a class of its own rather than a rule naming the
    // body it sits in, which is the fold's doing: an id outranks the
    // rule that hides a shut panel's body, so a display hung on
    // #spellsbody would go on laying these out under a title saying
    // the panel is closed. See the stylesheet.
    b.className = "spellbtn";
    // The picture, or the name when there is no picture for this
    // spell's key. That second case is not a defect to be guarded
    // against so much as a thing that will happen: a spell can be
    // added to the world before its glyph is drawn, and a browser
    // can be holding a bundle from before it was. Either way the
    // button still says what it casts and still casts it.

    const glyph = spellGlyph(sp.icon);
    if (glyph) b.appendChild(glyph);
    else {
      const named = document.createElement("span");
      named.className = "spellname";
      named.textContent = sp.name;
      b.appendChild(named);
    }
    // The cost, under the glyph and in the quiet colour. See above
    // for why it stayed when the name went.
    const cost = document.createElement("span");
    cost.className = "cost";
    b.appendChild(cost);
    // A pinned spell wears a pin in the top right corner, as a pinned kind
    // does in the bag, so it is plain why it comes first (2026-10-17).
    if (sp.pinned) {
      b.classList.add("pinned");
      const pin = document.createElement("span");
      pin.className = "pin";
      pin.setAttribute("aria-hidden", "true");
      b.appendChild(pin);
    }
    const entry = { b, cost, sp, ready: 0 };
    b.addEventListener("click", () => this.onCast(entry.sp));
    // A finger cannot hover, and the hover is now where the spell's
    // name lives, so the touchscreen gets the same dialog a long
    // press on a bag slot gets — the bag's gesture and the bag's box,
    // because a long press should mean one thing across the whole
    // page. longPress is the one in touch.ts and not a second timer
    // of our own: it is what knows that Android answers a hold with
    // a contextmenu and iOS answers it with nothing, and its swallow
    // is what keeps the release of a hold off the click listener
    // above. Two things have to hold for the press not to cast, and
    // both were checked in a browser: that swallow, and the fact
    // that the menu is over the finger by the time it lifts, so the
    // click a release mints is hit-tested against the menu and not
    // against this button. Either way the press opens the menu and
    // does not cast; the menu's own button casts.
    //
    // But for the light while one is burning, where the hold puts it out
    // (2026-10-17: "Long press light or the status condition to cancel the
    // spell"), and the menu waits until it is out.
    longPress(b, () => {
      if (entry.sp.putout) this.onLuxOut();
      else this.onSlotMenu(this.spellMenu(entry.sp));
    });
    return entry;
  }

  /** Paint what the server now says of a spell onto its button. */
  private paintSpell(entry: SpellButton, sp: SpellView) {
    entry.sp = sp;
    const b = entry.b;
    // Exactly one of the three, chosen from the server's two
    // sentences and nothing else: why is red, moot is gold, and
    // neither is green.
    b.classList.toggle("cannot", !!sp.why);
    b.classList.toggle("moot", !sp.why && !!sp.moot);
    b.classList.toggle("can", !sp.why && !sp.moot);
    // A monster's ability costs nothing, so where the cost would be it
    // counts down its wait instead: see paintAbilityClock.
    if (!sp.ability) entry.cost.textContent = String(sp.mana);
    // The name first, because it is not on the screen any more, and
    // then what it does, since that is what somebody reading the
    // panel for the first time wants, and then the refusal or the
    // reason it is moot, since that is what somebody looking at a
    // red or a gold one wants. The last two are the server's own
    // words: see above.
    const reason = sp.why || sp.moot;
    // A spell that is running wears what is left of it as a fill behind
    // the picture, draining from the right (2026-09-29: "when lasting
    // spell effects are enabled, show the duration remaining as part of
    // the button for the spell (like a background progress gradient)").
    // The server sends whole seconds and the stylesheet eases the fill
    // across each one, so it drains smoothly rather than in steps.
    const lasting = !!sp.left && !!sp.lasts;
    b.classList.toggle("lasting", lasting);
    b.style.setProperty("--left", lasting ? String(Math.min(1, sp.left! / sp.lasts!)) : "0");
    // A spell whose cooldown group is still cooling is dimmed, with a
    // shade over it that sweeps off as the wait runs out (2026-10-07:
    // "spells should have a cooldown"). The server sends when the group
    // is ready by its own clock, carried onto this page's by the offset
    // the steps are timed by, and the whole cooldown's length; the
    // stylesheet runs the sweep, started part of the way in by however
    // much of the wait has gone. It is a hint like the colours: a press
    // still says the words, and the world says how long is left.
    const coolLeft = sp.ready && sp.cool ? this.coolLeft(sp.ready, sp.cool) : 0;
    const cooling = coolLeft > 0;
    if (cooling && entry.ready !== sp.ready && b.classList.contains("cooling")) {
      // A new wait straight after the last: start the sweep over.
      b.classList.remove("cooling");
      void b.offsetWidth;
    }
    b.classList.toggle("cooling", cooling);
    if (cooling) {
      b.style.setProperty("--cool-ms", `${sp.cool}ms`);
      b.style.setProperty("--cool-delay", `${Math.round(coolLeft - sp.cool!)}ms`);
    }
    entry.ready = cooling ? sp.ready! : 0;
    if (sp.ability) this.paintAbilityClock(entry);
    const running = lasting ? ` ${sp.left} ${sp.left === 1 ? "second" : "seconds"} left of it.` : "";
    const pinned = sp.pinned ? " Pinned to the head of the panel." : "";
    const cost = sp.ability ? "" : `, ${sp.mana} mana`;
    const says = `${sp.name}${cost}. ${sp.look}${running}${pinned}`;
    b.title = reason ? `${says} ${reason}` : says;
    // A picture with no text in it says nothing to anything that
    // reads rather than looks, so the name and the cost are the
    // button's label in words.
    b.setAttribute("aria-label", `${sp.name}${cost}`);
  }

  /**
   * The Ride button's wait (2026-10-13: "show a timer for how long until a
   * mount can be used, in the button"): the whole seconds until the horse
   * will stand for you again after a fight, in small figures under the
   * horse's foot, the clock a status wears (2026-10-13: "The countdown
   * should use the same subscript thing like the light status"). The
   * server sends the moment the fight window closes, so the state stays
   * the same through the wait, and the number is ticked down here, at each
   * whole second, until it is gone.
   * The hover says the same, and the word the server sends on a press
   * still decides.
   */
  private paintRideWait() {
    window.clearTimeout(this.rideTimer);
    const off = this.game.clockOffset;
    const left = this.rideReady && off !== null ? this.rideReady + off - performance.now() : 0;
    const secs = Math.ceil(left / 1000);
    const waiting = secs > 0;
    this.rideBtn.classList.toggle("waiting", waiting);
    let n = this.rideBtn.querySelector<HTMLElement>(".clock");
    if (waiting && !n) {
      n = document.createElement("span");
      n.className = "clock";
      n.setAttribute("aria-hidden", "true");
      this.rideBtn.append(n);
    }
    if (n) n.textContent = waiting ? String(secs) : "";
    this.rideBtn.title = this.rideBtn.classList.contains("on")
      ? "Get down from your horse (/ride)"
      : waiting
        ? `Your horse will not stand for you in a fight: ${secs} ${secs === 1 ? "second" : "seconds"} until you can ride (/ride)`
        : this.rideNo
          ? `${this.rideNo} (/ride)`
          : "Get on your horse: three tiles a second, above ground only unless a wizard's staff is up in your hand, and any fight puts you on foot (/ride)";
    if (waiting) this.rideTimer = window.setTimeout(() => this.paintRideWait(), left - (secs - 1) * 1000 + 5);
  }

  /**
   * A monster's ability, for an admin playing it with /boss <monster>,
   * wears the whole seconds left of its wait where a spell's cost goes
   * (2026-10-27: "show the time to cooldown on the monster ability spell
   * button"), in the Ride button's figures, ticked down here at each
   * whole second as paintRideWait does, and nothing once it is ready.
   */
  private paintAbilityClock(entry: SpellButton) {
    window.clearTimeout(entry.tick);
    entry.tick = 0;
    const sp = entry.sp;
    const left = sp.ready && sp.cool ? this.coolLeft(sp.ready, sp.cool) : 0;
    const secs = Math.ceil(left / 1000);
    entry.cost.textContent = secs > 0 ? String(secs) : "";
    entry.cost.classList.toggle("clock", secs > 0);
    if (secs > 0 && entry.b.isConnected) entry.tick = window.setTimeout(() => this.paintAbilityClock(entry), left - (secs - 1) * 1000 + 5);
  }

  /**
   * How many milliseconds are left of a cooldown that ends at ready on the
   * server's clock, by this page's, no more than the whole cool. Without a
   * reading of the server's clock yet it is the whole of it.
   */
  private coolLeft(ready: number, cool: number): number {
    const off = this.game.clockOffset;
    if (off === null) return cool;
    return Math.max(0, Math.min(cool, ready + off - performance.now()));
  }

  /**
   * What a long press on a spell offers. It is the touchscreen's whole
   * answer to a button that is now a picture: the name, which used to be
   * written on the button and is now only in a hover no finger can reach,
   * what the spell does, why it is red or gold, and a way to cast it.
   *
   * It is the bag slot's menu, the same type and the same box on the
   * screen, and that is deliberate rather than convenient. A long press
   * already means "show me what I cannot hover for, and offer me what I
   * cannot right-click for" everywhere else on the page — the bag, the
   * depot, another player — and a spell wants exactly that and nothing
   * more. Inventing a second dialog with its own markup and its own way
   * of being dismissed would teach the player that a hold means one thing
   * here and another thing there, for no gain: the heading takes the name
   * and the cost, the line under it takes the description and the
   * server's reason, and the actions take the one thing there is to do.
   *
   * The action's label says what pressing it will actually do, which is
   * not always casting: a spell with an arg is half a sentence without a
   * name after it, and pressing it puts the words in the chat box to be
   * finished. It goes through the same onCast as a click, so there is
   * still exactly one route from this panel into a spell.
   *
   * A spell the server would refuse is offered anyway, coloured as the
   * action worth a second's thought — the same colour dropping a whole
   * pile wears. That is the panel's rule kept: red is a hint and never a
   * lock, the words go out on a press, and the refusal that counts is the
   * one that comes back in the console.
   */
  private spellMenu(sp: SpellView): SlotMenu {
    const reason = sp.why || sp.moot;
    return {
      title: sp.ability ? sp.name : `${sp.name} — ${sp.mana} mana`,
      look: reason ? `${sp.look} ${reason}` : sp.look,
      actions: [
        {
          label: sp.arg ? "Put the words in the chat box" : sp.ability ? "Use it" : `Cast it — say “${sp.words}”`,
          heavy: !!sp.why,
          go: () => this.onCast(sp),
        },
        // Pin or Unpin, so the spell goes to the head of the panel
        // (2026-10-17: "Support pinning spells"), as Pin does for a kind
        // in the bag's menu.
        { label: sp.pinned ? "Unpin" : "Pin", go: () => this.onPinSpell(sp, !sp.pinned) },
      ],
    };
  }

  /**
   * Everything currently true of the player, most urgent first. Each says
   * itself one way, in words — see showStatuses for why the initials and
   * the icons went.
   */
  private statusList(you: You): Status[] {
    const out: Status[] = [];
    if (you.dead) out.push({ full: "Dead", glyph: "dead", title: "You are dead.", cls: "bad" });
    // In a fight is the fight window (2026-10-07: "The rules for when you
    // can cast homecoming or log out should be the same"): a blow struck or
    // taken, or anything having you picked to attack, under ten seconds
    // ago, outside a protection zone. It was "In combat" and drawn from
    // the logout refusal, so it went whenever a skull was up; since
    // 2026-10-22 ("there should be a "in a fight" status icon") it is the
    // server's own count of the window, with an icon of its own, and it
    // stands beside a skull, since a skulled player in a fight is in one.
    // The clock is the seconds left: it holds at ten while something has
    // you picked and counts down once nothing does.
    if (you.fight) {
      out.push({
        full: `In a fight ${seconds(you.fight)}`, glyph: "fight", clock: String(you.fight), cls: "warn",
        title: `In a fight: a blow struck or taken, or something having you picked to attack, in the last ten seconds. Until it is ${you.fight === 1 ? "a second" : `${you.fight} seconds`} behind you, outside a protection zone, you cannot log out, call the Eagles, take a ship or the Elvenking's barrels, or light a pipe, and a lit pipe goes out. A protection zone ends it at once.`,
      });
    }
    // A skull is the one status with a clock on it, so it says the clock:
    // what a wearer wants to know is when the temple will have them back.
    // It reads m:ss, as Lux does (2026-10-15: "Show the skull timer in
    // mm:ss"): a skull runs minutes, and "312 seconds" is a sum to do.
    // Only a kill puts a skull on (2026-10-24: "A skull should appear only
    // if you have the timer running from killing a player"). An unjust
    // attack's lock has no clock to show (2026-10-22: "when you attack
    // someone unjustly, you get an untimed skull, no timer"): it is up
    // until the fight is over, and it has a padlock of its own. A skull
    // locks you too, so both show while one runs.
    if (you.skull) {
      out.push({
        full: `Skull ${mmss(you.skull)}`, glyph: "skull", clock: mmss(you.skull),
        title: `You killed somebody who was not protection zone locked. While the skull is up — ${mmss(you.skull)} more — you stay protection zone locked, and longer if a fight is still on when it runs out.`,
        cls: "bad",
      });
    }
    if (you.locked) {
      out.push({
        full: "Protection zone locked", glyph: "pzlock",
        title: you.skull
          ? "Protection zone locked: no protection zone will take you, you cannot log out, and your eyes burn red, until your skull runs out and you are out of the fight."
          : "Protection zone locked: you attacked somebody who was not. It has no timer: until you are out of the fight no protection zone will take you, you cannot log out, and your eyes burn red. Kill somebody who is not locked and you wear a skull for ten minutes.",
        cls: "bad",
      });
    }
    // On a horse is not a status of its own since 2026-10-13: the ride
    // button stands just left of these, lit while you ride, and a second
    // horse's head beside it would only say the same thing again.
    if (you.pz) {
      out.push({
        full: "Protected", glyph: "protected", cls: "good",
        title: "A protection zone: no fighting, no hunger, and you may always log out or call the Eagles. Your wounds close here without spending a meal, and an empty stomach costs you nothing while you stand on it.",
      });
    }
    // Every protection zone taken away from everybody by an admin
    // (2026-10-27: "add a command to remove pz server wide"), for as long
    // as it lasts. See game/zonesoff.go.
    if (you.zonesoff) {
      out.push({
        full: "No protection zones", glyph: "nopz", cls: "bad",
        title: "No protection zones: an admin has taken them away from everybody. No ground in the world is safe, the village included: anybody may attack you anywhere, and monsters walk into the zones, until they are given back.",
      });
    }
    // The other status with a clock on it. What matters about a spell that
    // runs out is how long is left of it, so the line says. Twenty seconds is short enough that the number is
    // the whole of what a player wants from this line — it is not "you are
    // hasted", it is "you have four seconds of it left, spend them" — and
    // it doubles as the clock on the button, since the world will not cast
    // a second haste over the first and the panel goes gold again the
    // moment this reaches nought. What the spell is worth is deliberately
    // not restated here: the Speed row already reads the pace being walked
    // at, so a number in this hover would be the same fact written down a
    // second time in another panel, and the second copy is the one that
    // goes stale.
    if (you.haste) {
      out.push({
        full: `Hasted ${seconds(you.haste)}`, glyph: "haste", clock: String(you.haste),
        title: `Haste: for ${you.haste} more seconds you walk ten levels faster than your level alone would carry you, or ride a tenth faster, though not past the limit a PZ lock puts on you. The Speed row is showing the pace you are actually walking at. It cannot be cast again until this runs out.`,
        cls: "good",
      });
    }
    // And the fourth, the lamp. The number is the whole of the line, as
    // with the haste, and here it is the one clock in the game that
    // matters at the moment it runs out rather than before: when it
    // reaches nought the dark comes back to two tiles with whatever was
    // standing in it. What the light is worth is not restated in the
    // hover beyond the one sentence, since the screen itself is showing
    // it — five tiles of cave lit instead of two is not a number anybody
    // needs told.
    // Named for its words and read as a clock, "Lux 1:59", the way the
    // food bar reads (asked for on 2026-09-19: 'show the light spell status as
    // "Lux m:ss"'). A clock is not an abbreviation: nothing in it stands
    // for a word. The other clocks but the skull's say their seconds in
    // the word, since twenty seconds of haste is a count and two minutes
    // of light is a countdown — and the hover is a clock too, for the same reason: a
    // lamp just lit has 119 seconds on it, which is a number to do
    // arithmetic on rather than to read.
    //
    // The hover used to end "It cannot be cast again until this runs
    // out", which was never true of this spell — a light is lit over a
    // light, and the button is gold rather than red while one is
    // burning — so it said the opposite of what the server does.
    if (you.lux) {
      out.push({
        full: `Lux ${mmss(you.lux)}`, glyph: "lux", clock: mmss(you.lux),
        title: `Light: ${mmss(you.lux)} left, and for that long you see five tiles into the dark instead of two. When it runs out the dark closes back in. Saying the words again over a light already burning starts its two minutes over, for the ten mana. Hold this or the light's button to put it out.`,
        cls: "good",
        hold: () => this.onLuxOut(),
      });
    }
    // Smoking (2026-10-17: "add a smoking condition/status icon"): a lit
    // pipe of Longbottom Leaf, read as a clock like the lux, and shown
    // only while it is doing you good, since that is when it is worth
    // knowing how long it has left.
    if (you.pipe) {
      out.push({
        full: `Smoking ${mmss(you.pipe)}`, glyph: "pipe", clock: mmss(you.pipe),
        title: `Smoking: this pinch has ${mmss(you.pipe)} left to burn, and while the pipe burns your wounds mend twice as much and your mana comes back twice as fast. Sit still and the next pinch in your bag goes in by itself. Taking a step or being drawn into a fight puts it out; being shoved or dragged does not.`,
        cls: "good",
      });
    }
    // Dragon ham's ward (2026-10-23), read as a clock like the pipe.
    if (you.firemeal) {
      out.push({
        full: `Dragon-fed ${mmss(you.firemeal)}`, glyph: "firemeal", clock: mmss(you.firemeal),
        title: `Dragon-fed: the dragon ham you ate has ${mmss(you.firemeal)} left in it, and until then a quarter comes off every fire wound you take, on top of what dragon-scale mail takes. Eating another starts it over.`,
        cls: "good",
      });
    }
    // A temporary rate (2026-10-22: "allow admin to modify a server
    // training/exp multiplier. it should show up on the login screen and
    // as a condition"; 2026-11-03: "just have /rate M, and /temprate N D
    // and the overall rate is M*N, and the condition status shows N to
    // players"): up while an admin has one, good above 1 and a warning
    // below. The server's own rate is its pace and not shown here.
    // The time left is in its words and its hover, and the clock on the
    // icon stays the rate, which is the thing it is.
    if (you.rate && you.rate !== 1) {
      const r = you.rate;
      const left = you.rateleft ? rateWait(you.rateleft) : "";
      out.push({
        full: left ? `Rate x${r} ${left}` : `Rate x${r}`, glyph: "rate", clock: `x${r}`,
        title: `Rate x${r}: an admin has experience and skill training ${r > 1 ? "faster" : "slower"} for everybody for a while. Every monster is worth ${r} times what it is on this server, and every use of a skill, magic's mana included, counts ${r} times.${left ? ` It runs out in ${left}.` : ""}`,
        cls: r > 1 ? "good" : "warn",
      });
    }
    // Poison, asked for on 2026-09-23 with the giant spider's venom. It
    // reads as the dose, "Poisoned 6", because the dose is the whole of
    // it: the next beat takes that many and each after takes one fewer,
    // so the number is both how hard it is biting and how long it has
    // left. It is the one status here that the player can end by saying
    // something, so the hover says what.
    if (you.poison) {
      const beats = you.poison;
      const total = (beats * (beats + 1)) / 2;
      out.push({
        full: `Poisoned ${beats}`, glyph: "poison", clock: String(beats),
        title: `Poisoned: the next beat takes ${beats}, and every two seconds after that one fewer — ${total} more in all unless a spider bites again, which puts it back up. Armor is no answer to it. Athelas draws all of it out at once; a protection zone does not stop it biting.`,
        cls: "bad",
      });
    }
    // Burning (2026-10-09), off a temple shade's dying flare: the poison's
    // ladder in fire, read the same way, with nothing to say against it.
    if (you.burning) {
      const beats = you.burning;
      const total = (beats * (beats + 1)) / 2;
      out.push({
        full: `Burning ${beats}`, glyph: "burning", clock: String(beats),
        title: `Burning: the next beat takes ${beats}, and every two seconds after that one fewer — ${total} more in all. Armor is no answer to it, and nothing puts it out but time; standing in a protection zone keeps it from biting while it burns down.`,
        cls: "bad",
      });
    }
    // Paralysed by the Witch-king's shriek, and entangled in Shelob's web
    // (2026-10-12): clocks, since what matters about them is when they let
    // go.
    if (you.paralysed) {
      out.push({
        full: `Paralysed ${seconds(you.paralysed)}`, glyph: "paralysed", clock: String(you.paralysed),
        title: `Paralysed: for ${you.paralysed} more seconds you cannot move, strike or cast an attack. You can still drink potions and cast heal.`,
        cls: "bad",
      });
    }
    if (you.entangled) {
      out.push({
        full: `Entangled ${seconds(you.entangled)}`, glyph: "entangled", clock: String(you.entangled),
        title: `Entangled: for ${you.entangled} more seconds you cannot move or levitate. You can still fight, cast and drink.`,
        cls: "bad",
      });
    }
    // Slowed by Sauron's rift (2026-10-27).
    if (you.slowed) {
      out.push({
        full: `Slowed ${seconds(you.slowed)}`, glyph: "slowed", clock: String(you.slowed),
        title: `Slowed: for ${you.slowed} more seconds you walk at half your pace. The Speed row is showing the pace you are actually walking at.`,
        cls: "bad",
      });
    }
    // And the fifth and sixth, the two ends of the blood bond, which
    // are the one thing on this list that is about somebody else. So they
    // are the ones that name people: "Bonded to Bob 2:14" rather than
    // "Bonded", because the whole of what a player needs from this line at
    // a glance is whose wounds are about to be theirs — or whose blood is
    // standing under their own. Read as clocks like the lux, since three
    // minutes is a countdown rather than a count.
    //
    // Both can be up at once, and then both chips are shown, because they
    // pull the health bar in opposite directions and a player carrying one
    // friend while a second carries them is entitled to know which is
    // which. They are two chips rather than one combined line for the same
    // reason: nothing about them is one fact.
    //
    // The names come off the wire rather than out of the creature list.
    // The server is the only thing that knows, and since a bond no longer
    // breaks when the two of you walk apart the other end is routinely
    // nowhere near the screen — a line reading "bonded to somebody" would
    // be worse than the name.
    //
    // The one you are holding up is "warn" and the ones held up on you are
    // "good", which is the honest colouring: carrying somebody is a thing
    // that can kill you, and being carried is a gift.
    if (you.bond) {
      out.push({
        full: `Bonded to ${you.bondwith} ${mmss(you.bond)}`, glyph: "bond", clock: mmss(you.bond),
        title: `Blood bond: for ${you.bond} more seconds you are carrying ${you.bondwith}'s wounds. A share of every wound they take opens on you instead of them — half of it if you are the only one bonded to them, less each if others are too — and nothing of yours ever crosses back to them. Healing never crosses either. Say the words at ${you.bondwith} again to take it off, which costs nothing; say them at somebody else and the bond moves there. Walking apart does not end it, and nothing crosses while either of you is in a protection zone. It ends if either of you strikes the other, or dies, or leaves the world. You can die of a fight you were never in.`,
        cls: "warn",
      });
    }
    // The share is worked out here rather than sent, because it is head
    // count and the heads are on the wire: you and everyone bonded to you.
    if (you.bonded && you.bondedby?.length) {
      const by = you.bondedby;
      const heads = by.length + 1;
      const part = ["", "", "half", "a third", "a quarter", "a fifth", "a sixth"][heads] ?? `one part in ${heads}`;
      out.push({
        full: `Bonded by ${by.join(", ")} ${mmss(you.bonded)}`, glyph: "bond", clock: mmss(you.bonded),
        title: `Blood bond: ${by.length === 1 ? `${by[0]} is` : `${by.join(", ")} are`} carrying your wounds, ${you.bonded} seconds left on the longest of ${by.length === 1 ? "it" : "them"}. Every wound you take is divided by heads with you counting as one, so you keep ${part} of it and each of them takes ${part}. Healing does not cross: you close your own, and nothing crosses while either end is in a protection zone. They can die of this, and their own health bars are where their shares show. You cannot end it — it is theirs to take off — and striking one of them ends that one.`,
        cls: "good",
      });
    }
    // A great ring's fading (2026-10-05: "Implement the lesser, Seven,
    // Nine and One Ring"): how much of you it has taken, as a clock
    // of its own, and which way it is going. It is "bad" while the ring is
    // on and taking more, and "warn" while it is off and you are coming
    // back, since the health bar is short either way.
    if (you.fading) {
      const going = !!you.fadingon;
      out.push({
        full: `Fading ${you.fading}%`, glyph: "fade", clock: `${you.fading}%`,
        title: you.fadingone
          ? `Fading: the One Ring has taken ${you.fading} parts in a hundred of you, and goes on taking you, worn or not, until its week ends and you fade wholly away and die. Unmake it at the Cracks of Doom first.`
          : going
          ? `Fading: your great ring has taken ${you.fading} parts in a hundred of your health, and takes another every minute it is worn (every three for a hobbit or a dwarf), down to 30. Take it off and they come back, one a minute.`
          : `Coming back: ${you.fading} parts in a hundred of your health are still gone to a great ring. One comes back every minute it stays off.`,
        cls: going ? "bad" : "warn",
      });
    }
    // Making noise is the ordinary state of anybody walking about — a
    // body by itself is three tiles of it, before any gear — and the
    // Clank row says how much. What is worth a chip of the line is being
    // quieter than being there at all, because below nought is where
    // whole kinds of monster stop hearing you however close you stand,
    // and the way you play changes. The row itself is where the number
    // lives, so this only has to say that it is happening.
    if (you.clank < 0) {
      out.push({
        full: "Sneaking", glyph: "sneak", cls: "good",
        title: `Your gear is quieter than being there at all: your clank is ${you.clank}, so no monster hears you at any distance unless something has already angered it. Most of the bestiary has no keenness of its own — a rat notices what is standing on it and nothing more — and only a dwarf geomancer, a wight, a giant spider, a dragon or the Balrog has enough of a reach to find you through this.`,
      });
    }
    // What the target is being done with, since a single ring and a double
    // one are easy to miss in a crowd: fought from here, chased, or followed.
    if (you.target) {
      const t = this.game.creatures.get(you.target);
      // One of the world's people is never the target, so there is no
      // line for following one: they were followed until 2026-10-07
      // ("don't allow npcs to be followed").
      // Out of sight, the server keeps it picked for ten seconds since it
      // was last seen, and does nothing after it until it is back
      // (2026-10-16: "Keep the target for up to 10s since last seen").
      if (!t) {
        out.push({ full: "Target out of sight", glyph: "target", title: "Your target is out of sight: it stays picked for ten seconds since you last saw it, and is fought or followed again the moment it is back. After that it is let go of.", cls: "warn" });
      } else if (t.kind === "player" && !t.hostile) {
        out.push({ full: "Following", glyph: "chase", title: "Following a player: you walk after them wherever they go. Click them again to let go.", cls: "good" });
      } else if (you.chase) {
        out.push({ full: "Chasing", glyph: "chase", title: "Chasing your target: you walk after it, and a step of your own only pauses that. Pick it again to let go, or switch chase off.", cls: "warn" });
      } else {
        out.push({ full: "Attacking", glyph: "target", title: "Fighting your target from where you stand: it is hit whenever it is in reach. Chase is switched off; switch it on to walk after it.", cls: "warn" });
      }
    }
    // The stance, when it is not the white hand everybody starts with:
    // red is the one that can earn a skull, so it wears the red.
    if (you.stance === "red") {
      out.push({ full: "Red stance", glyph: "stancered", title: stanceMeaning(you.stance), cls: "bad" });
    } else if (you.stance === "yellow") {
      out.push({ full: "Yellow stance", glyph: "stanceyellow", title: stanceMeaning(you.stance), cls: "warn" });
    }
    // The auto stance gets no chip (2026-10-08: "don't show a status icon
    // for current auto attack status"): the Auto button and the Auto row
    // already say which it is.
    // Being fed is the ordinary state and the bar already says so. Hunger is
    // only worth flagging when it is costing something: wounded with nothing
    // left to mend them.
    if (you.food === 0 && you.hp < you.maxhp) {
      out.push({ full: "Hungry", glyph: "hungry", title: "Wounded and out of food: nothing is closing them until you eat.", cls: "bad" });
    }
    // What an admin has switched on for themselves (2026-10-27: "there
    // should be a condition/status icon to tell if i'm
    // invisible/invincible etc."), in the admin's own colour, each saying
    // the command that switches it off. Nobody else is sent any of it.
    out.push(...adminStatuses(you.adm));
    // The time of day (2026-10-26: "add a condition that shows time of day
    // so we know if it's night or day"): a sun or a moon, last on the line
    // since it is always there, with the time left until the sky turns as
    // its clock. It shows under the ground too, where it changes nothing,
    // because that is where somebody wants to know what they will come up
    // into. A world without the cycle has no night and no icon.
    const sky = this.skyStatus();
    if (sky) out.push(sky);
    return out;
  }

  /** The time of day above the ground, or nothing in a world without
   *  night. See statusList. */
  private skyStatus(): Status | undefined {
    const g = this.game;
    if (!g.skyTurn && !g.skyHeld) return undefined;
    const night = g.night;
    // Day is blue like the other good things, night a warning: the
    // surface is a cave's then, and what you cannot see can see you.
    const cls = night ? "warn" : "good";
    const what = night
      ? "the sky lights nothing above the ground, which is as dark as a cave: you see past your dark sight only what stands in a light, so carry a torch or say the light's words, and a light carried can be heard."
      : "the sky lights everything above the ground.";
    const below = (g.me()?.z ?? 0) < 0 ? " You are under the ground, where it is always dark; this is the time up above." : "";
    if (g.skyHeld) {
      return {
        full: night ? "Night" : "Day", glyph: night ? "night" : "day", cls,
        title: `${night ? "Night" : "Day"}: ${what} An admin is holding it at ${night ? "night" : "day"}, so it will not turn until they give it back to the clock.${below}`,
      };
    }
    const left = mmss(g.skyTurn);
    return {
      full: `${night ? "Night" : "Day"} ${left}`, glyph: night ? "night" : "day", clock: left, cls,
      title: night
        ? `Night: ${what} Day breaks in ${left}, and lasts ten minutes.${below}`
        : `Day: ${what} Night falls in ${left}, and lasts five minutes.${below}`,
    };
  }

  /**
   * Fits the statuses into the one line they have. Written out in full first,
   * then initials, then icons — whichever is the most readable form that does
   * not overflow. Measuring beats counting: a long "Fed 5:59" costs more room
   * than two short labels, and the panel can be any width.
   */
  /**
   * The last round trip to the world, in milliseconds, or null while there
   * is nothing to report — not connected, or nothing has come back yet.
   * It is coloured by what it costs you: under 100 nothing notices, past
   * 300 a step and a swing both arrive late, and the number is there so a
   * bad connection is read off the panel rather than blamed on the game.
   */
  setPing(ms: number | null) {
    if (ms === null) {
      this.ping.textContent = "–";
      this.ping.className = "";
      return;
    }
    this.ping.textContent = `${ms} ms`;
    this.ping.className = ms < 100 ? "good" : ms < 300 ? "fair" : "bad";
  }

  /**
   * The statuses, always written out in full.
   *
   * They used to have three forms and the row picked between them by
   * measuring: the words, then initials, then bare icons. It meant the
   * line never overflowed, and it meant that the moment a fourth status
   * turned up — which is the moment the most is happening to you — the
   * row answered by becoming CBT, SK300, PZ and a picture of an eye. A
   * status nobody can read is not a shorter status, it is a missing one,
   * and the states these announce (in a fight, skulled, about to be able
   * to log out) are exactly the ones worth reading at a glance rather
   * than hovering for.
   *
   * So there is one form, and when it does not fit on a line it takes
   * another. The panel is a column and has the height to spare; what it
   * never had was the width.
   *
   * And the one form is never abbreviated, in any part: the word, the
   * unit and the name are all written out (see Status). The initials
   * were taken off the row on purpose, and "20s" and "PvP" are initials
   * that crept back in one letter at a time.
   */
  private showStatuses(you: You) {
    // Icons over the world since 2026-10-01 ("show statuses as icons to
    // the right of the info icon. remove the statuses field from the stats
    // panel"). The words above still hold for what each one is: written
    // out in full, as its tooltip and as the name it is read out by. A
    // status with a clock keeps the clock as a figure on its icon, since
    // how long is left is the whole point of those.
    const list = this.statusList(you);
    const seen = new Set<string>();
    const els = list.map((st) => {
      // By glyph, and by which of that glyph it is: carrying somebody and
      // being carried are two chains at once.
      let key = st.glyph;
      for (let n = 2; seen.has(key); n++) key = `${st.glyph}#${n}`;
      seen.add(key);
      let e = this.statusEls.get(key);
      if (!e) {
        // A button, since a press says what it is (2026-10-01: "clicking a
        // status icon should tell you what it is"): the tooltip is for a
        // mouse that waits, and a finger never gets one.
        const el = document.createElement("button");
        el.type = "button";
        const clock = Object.assign(document.createElement("span"), { className: "clock" });
        const made = { el, clock, st };
        el.addEventListener("click", () => this.addLine(`${made.st.full}: ${made.st.title}`, "answer"));
        // Only a status with something to do takes the hold; the rest
        // leave a right-click and a held finger alone.
        longPress(el, () => made.st.hold?.(), () => !!made.st.hold);
        el.append(viewGlyph(st.glyph));
        this.statusEls.set(key, made);
        e = made;
      }
      e.st = st;
      e.el.className = `statusicon ${st.cls}`;
      e.el.title = st.title;
      e.el.setAttribute("aria-label", st.full);
      if (st.clock) {
        e.clock.textContent = st.clock;
        if (!e.clock.isConnected) e.el.append(e.clock);
      } else e.clock.remove();
      return e.el;
    });
    for (const k of [...this.statusEls.keys()]) if (!seen.has(k)) this.statusEls.delete(k);
    // Put back only when the set or the order changed, so an icon under a
    // finger stays where it is while its clock ticks.
    const now = Array.from(this.statuses.children);
    if (now.length !== els.length || els.some((el, i) => now[i] !== el)) this.statuses.replaceChildren(...els);
  }

  /** The XP rate and Next level rows, from the last fifteen minutes. */
  private showXPRate(you: You) {
    const now = performance.now();
    this.xpRate.note(you.name, you.xp, now);
    const rate = this.xpRate.perHour(now);
    const left = you.xpnext - you.xp;
    const rateText = rate === undefined ? "\u2013" : `${num(Math.round(rate))} / h`;
    const nextText = rate === undefined || rate <= 0 ? "\u2013" : roughWait((left / rate) * 3600);
    const watched = this.xpRate.watchedMins(now);
    const key = `${rateText}/${nextText}/${watched}`;
    this.showSkillRates(you, now);
    if (key === this.lastXPRateKey) return;
    this.lastXPRateKey = key;
    $("xprate").textContent = rateText;
    $("xpnexttime").textContent = nextText;
    $("xpraterow").title =
      (rate === undefined
        ? "Watching your experience: the rate shows once a minute has gone by. "
        : `${num(Math.round(rate))} experience an hour, from the last ${watched === 1 ? "minute" : `${watched} minutes`}. `) +
      "Counted over the last fifteen minutes on the clock, standing about included, so it is how well your hunting pays right now. It starts over when you log in or die.";
    $("xpnextrow").title = `${num(left)} experience to level ${you.level + 1}, at the XP rate above.`;
  }

  /** Each skill's time to its next level, at the pace of its last fifteen
   *  minutes, on the end of its hover. Nothing while it has not moved. */
  private showSkillRates(you: You, now: number) {
    for (const s of you.skills) {
      let w = this.skillRates.get(s.key);
      if (!w) this.skillRates.set(s.key, (w = new RateWatch()));
      w.note(you.name, s.uses, now);
    }
    for (const r of this.skillRows) {
      const cur = you.skills.find((s) => s.key === r.s.key) ?? r.s;
      // Worked out again once a minute, or on a new level, rather than on
      // every use: a pace measured to the second read differently each
      // time the pointer came back to it (2026-10-26: "the tooltip for
      // training progress isn't stable and keeps changing").
      if (now - r.etaAt >= 60000 || cur.level !== r.etaLevel) {
        const rate = this.skillRates.get(r.s.key)?.perHour(now);
        const left = cur.next - cur.uses;
        const mins = this.skillRates.get(r.s.key)!.watchedMins(now);
        r.eta = rate && rate > 0 && left > 0 ? ` At the pace of the last ${mins === 1 ? "minute" : `${mins} minutes`}, level ${cur.level + 1} in ${roughWait((left / rate) * 3600)}.` : "";
        r.etaAt = now;
        r.etaLevel = cur.level;
      }
      const title = r.base + r.eta;
      if (r.row.title !== title) r.row.title = title;
    }
  }

  update(you: You) {
    const skillKey = you.skills.map((s) => `${s.key}${s.level}.${s.pct}${s.boosts?.map((b) => `+${b.levels}${b.from}`).join("") ?? ""}`).join(",");
    // In the time it will really last this character: half as long again
    // for a hobbit (2026-09-29), whose stomach empties slower.
    const foodTotal = Math.round((you.food + you.inv.reduce((n, it) => n + it.count * (this.game.itemDefs.get(it.id)?.food ?? 0), 0)) * (you.foodmul ?? 1));
    const youKey = `${foodTotal}/${you.hp}/${you.maxhp}/${you.projected}/${you.mana}/${you.projectedmana}/${you.maxmana}/${you.haste}/${you.lux}/${you.pipe}/${you.poison}/${you.poisonleft}/${you.burning}/${you.paralysed}/${you.entangled}/${you.slowed}/${you.bond}/${you.bondwith}/${you.bonded}/${you.bondedby?.join(",")}/${you.level}/${you.xp}/${you.atkmin}/${you.atk}/${you.atkbig}/${you.def}/${you.defworn}/${you.held}/${you.speed}/${you.clank}/${you.dark}/${you.blind}/${you.wraith}/${you.atkcalc}/${you.defcalc}/${you.clankcalc}/${you.hpregenms}/${you.hpregenformulams}/${you.hpregenwhy}/${you.manaregenms}/${you.manaregenformulams}/${you.manaregenwhy}/${you.food}/${you.pz}/${you.zonesoff}/${you.canlogout}/${you.logoutwhy}/${you.fight}/${you.stance}/${you.autostance}/${you.chaseon}/${you.horse}/${you.riding}/${you.rideready}/${you.debug}/${you.skull}/${you.locked}/${you.dead}/${you.foodmul}/${this.game.night}/${this.game.skyTurn}/${this.game.skyHeld}/${(this.game.me()?.z ?? 0) < 0}/${skillKey}`;
    this.xpRateYou = you;
    this.showXPRate(you);
    if (youKey !== this.lastYouKey) {
      this.lastYouKey = youKey;
      $("pname").textContent = you.name;
      $("plevel").textContent = `Level ${you.level}`;
      $("hpbar").style.width = `${(100 * you.hp) / you.maxhp}%`;
      // The healing a potion still owes, laid along the same bar: it starts
      // where the solid fill stops and runs to where the window will leave
      // you. Written as a start and a length rather than as a second bar
      // drawn from zero and covered up, so the two never paint over each
      // other and the arithmetic says nothing when there is nothing to say
      // — with no window running the server sends projected equal to hp,
      // the length comes out at zero, and the panel is the panel it has
      // always been without a line here asking whether that is the case.
      // The text stays the two numbers it was: where you are is the fact
      // you act on, and where you are going is what the shade is for.
      $("hpproj").style.left = `${(100 * you.hp) / you.maxhp}%`;
      $("hpproj").style.width = `${(100 * Math.max(0, you.projected - you.hp)) / you.maxhp}%`;
      // The poison's share, laid over the end of the fill: the health a
      // running dose will still take, in the green its beats land in, and
      // nothing wide when there is no dose (see render.ts healthBar).
      const venom = Math.min(you.hp, Math.max(0, you.poisonleft ?? 0));
      $("hppoison").style.left = `${(100 * (you.hp - venom)) / you.maxhp}%`;
      $("hppoison").style.width = `${(100 * venom) / you.maxhp}%`;
      $("hptext").textContent = `${num(you.hp)} / ${num(you.maxhp)}`;
      // Mana sits under health because it is the same kind of fact: a pool
      // that empties and comes back. It is drawn even at level one, where
      // five of it will not cast anything — what it says then is that there
      // is something here to grow into.
      $("manabar").style.width = `${you.maxmana > 0 ? (100 * you.mana) / you.maxmana : 0}%`;
      // And a mana draught's window drawn past it the way a health
      // potion's is, since mana potions pay out over time too.
      $("manaproj").style.left = `${you.maxmana > 0 ? (100 * you.mana) / you.maxmana : 0}%`;
      $("manaproj").style.width = `${you.maxmana > 0 ? (100 * Math.max(0, (you.projectedmana ?? you.mana) - you.mana)) / you.maxmana : 0}%`;
      $("manatext").textContent = `${num(you.mana)} / ${num(you.maxmana)} mana`;
      const span = you.xpnext - you.xpbase;
      const frac = span > 0 ? (you.xp - you.xpbase) / span : 0;
      $("xpbar").style.width = `${Math.max(0, Math.min(100, frac * 100))}%`;
      $("xptext").textContent = `${num(you.xp)} xp · ${num(you.xpnext - you.xp)} to next`;
      // Attack is the roll a swing makes: every swing rolls anywhere from
      // the bottom to the top of this, evenly. It was written as the range,
      // "0–12", until 2026-09-23 ("since the lower bound on attack and
      // defense is always 0, just show the maximums in the stats panel"),
      // and is again since 2026-10-07: "show attack as the range of the
      // hit, including if the lower bound is 0". The bottom is the server's,
      // not assumed, and since later that day it is half the top, rounded
      // up ("change attack to be from half max to max"), and a fifth of
      // it since 2026-10-27: a sword at 24 reads 5–24, and bare hands 1–2.
      $("atk").textContent = `${you.atkmin ?? 0}–${you.atk}`;
      // And the spear's harder roll against Large and Huge creatures after
      // it, when the weapon in hand has one (2026-10-17: "show the range in
      // the attack field in the info panel. so attack 13-25 (X-Y vs
      // large/huge)"), on the next line, right-aligned across the whole row
      // (later that day: "put the extra attack damage range in brackets
      // right aligned on the next line, but no indent").
      $("atkbig").hidden = !you.atkbig;
      $("atkbig").textContent = you.atkbig ? `(${you.atkbigmin ?? 0}–${you.atkbig} vs large/huge)` : "";
      $("atkrow").title = calcTitle(ATTACK_ABOUT, you.atkcalc);
      // Defense is two ranges, because it is two numbers. The armor is
      // rolled against every blow from what is worn to twice it, so it has
      // a floor; the shield and the parry are rolled against every blow
      // that gets past it from nought to what is held, so they lift the top
      // and never the bottom. What is held was a pool, spent by every blow
      // in two seconds and drawn as a bar, until 2026-10-08 ("remove the
      // defence pool, apply it to every incoming attack").
      //
      // The two ends of the armor range are soakFloor and soakCeil on the
      // server: half what is worn, rounded up, and all of it, since
      // 2026-10-23 ("Make armour remove armour/2 to armour instead of
      // armour to double armour"), when every piece's number was doubled.
      // From 2026-09-23 it was worn to twice worn.
      const floor = Math.ceil(you.defworn / 2);
      // Written as the roll alone since 2026-10-08, "7–14", the way Attack
      // and Defense are ("show armor as a range like attack and defence are
      // shown. can it say anything other than "soaks" anyway?"). Bare skin
      // is a plain 0, as an empty hand is on the Defense row.
      $("armor").textContent = floor ? `${floor}–${you.defworn}` : "0";
      // On the panel at every width and whatever the debug switch says,
      // since 2026-10-07: "show armor in info panel without requiring debug
      // mode to be on". A phone used to lose it to the switch. The hover
      // spells out both — the figure, what the armor on every piece you
      // wear comes to, and the roll it makes.
      $("armorrow").title =
        `Your armor is ${you.defworn}: natural armor plus the armor on your back, the cloak over it, the helm on your head and a ring of protection. ` +
        `Every blow loses ${floor ? `anywhere from ${floor} to ${you.defworn}` : "nothing"} to it first, rolled afresh every blow, ` +
        `and a suit is on your back whichever way a blow comes, so it has a floor where your defense does not.`;
      // The defence row is the range what is held is rolled in (asked for
      // on 2026-09-19 as the most the pool could roll, and as a range since
      // 2026-10-07: "show defense as the range too"). Every blow past the
      // armor meets a roll from a fifth of this, rounded down, to all of it,
      // evenly, the way a blow rolls (2026-11-02: "defense should be rolled
      // 0.2 to top like attack", rounded down: "actually just round
      // defense down"; from nought until then): "1–8". Nothing
      // held is a plain 0, the way bare skin is on the Armor row.
      const heldLo = Math.floor(you.held / 5);
      $("defence").textContent = you.held <= 0 ? "0" : heldLo === you.held ? `${you.held}` : `${heldLo}–${you.held}`;
      $("defencerow").title = calcTitle(DEFENCE_ABOUT, you.defcalc);
      // Tiles a second, which is the only form of this number anybody can
      // picture. The row used to carry the speed the formula is written in
      // — 220, 225, 230 — with the tiles a second hidden in the tooltip,
      // and that is the wrong way round: the unit is Tibia's and means
      // nothing outside it, while "one and a half tiles a second" is a
      // thing you can hold a monster's pace up against. The unit goes in
      // the tooltip, where somebody comparing themselves to the level
      // formula can still find it.
      //
      // It is worked out from stepms rather than from the speed, because
      // stepms is what the walking actually uses: the two differ by a
      // rounding — a speed of 150 is a tile every 666 ms, not 666.67 —
      // and the number on the panel should be the one your feet are
      // keeping to rather than the one the formula asked for.
      // Named for what it is rather than "tiles", which is the name of
      // the shared wording helper at the top of this file: one of them is
      // a pace and the other is a distance, and the collision only shows
      // up as a type error if the two ever meet in one scope.
      const perSec = you.stepms ? 1000 / you.stepms : 0;
      $("speed").textContent = `${perSec.toFixed(1)} t/s`;
      if (you.stepms) {
        $("speedrow").title =
          `How fast you walk: ${perSec.toFixed(2)} tiles a second, which is a tile every ` +
          `${you.stepms} ms. In the unit the level formula is written in that is ${you.speed}: ` +
          `150 at level 1, whatever your race, closing on a horse\u2019s 300 with every level and never reaching it, a hundred of it being a tile a second. ` +
          `A diagonal step costs two and a half of a straight one.`;
      }
      // The two regeneration rates, behind the debug switch with Speed and
      // Clank (2026-10-07: "show hp and mana regen rate when debug is on").
      // They are the server's, worked out from the clocks that do the
      // mending, so all that happens here is the wording.
      $("hpregen").textContent = regenText(you.hpregenms, you.hpregenwhy);
      $("hpregenrow").title = regenTitle(
        "health",
        you.hpregenms,
        you.hpregenformulams,
        you.hpregenwhy,
        "It comes faster every level, only while you are fed — but in a protection zone fed or not, without touching your meal. An empty stomach outside one costs a point every ten seconds instead. A lit pipe doubles it while you sit out of a fight.",
      );
      $("manaregen").textContent = regenText(you.manaregenms, you.manaregenwhy);
      $("manaregenrow").title = regenTitle(
        "mana",
        you.manaregenms,
        you.manaregenformulams,
        you.manaregenwhy,
        "It comes faster every level, only while you are fed, and never inside a protection zone, where the clock is held until you step out. A lit pipe doubles it while you sit out of a fight.",
      );
      // Clank sits under the speed because it is the other half of the
      // same question — how you get about the world without being eaten —
      // and because it is a number about you rather than about any one
      // monster. Said as a distance rather than as a signed number,
      // because that is what it is: "3 tiles" is the ground a rat hears
      // you across, where "+3" was an adjustment to something the panel
      // never showed and read as a bonus. Nought is a fact worth showing
      // rather than a blank — it is what a hobbit in a mithril shirt
      // made until the hobbit's body went from 2 to 3 (2026-10-12).
      //
      // A negative is still drawn honestly if one ever turns up. Nothing
      // in the item table reaches below nought any more, since the cloak
      // takes away exactly what a body makes, but the panel is not the
      // place to assume that.
      $("clank").textContent = tiles(you.clank);
      // The meaning, short, and then the sum (2026-10-08). The long account
      // of which monsters hear how far is the help's Clank entry.
      $("clankrow").title = calcTitle(CLANK_ABOUT, you.clankcalc);
      // Sight is the player's side of the monsters' DarkVision and
      // LightVision (2026-10-19: "show vision (light/dark) as a field in
      // stats"): in the dark your dark sight, in a light as far as the
      // world tells you of anybody, the server's viewR. Past your dark sight
      // it is their clank and your alertness that decide, as the hover says.
      // And wraith-sight while a Great Ring is on (2026-10-19: "add
      // WraithSight"), the one thing that sees the faded.
      // And blind sight across the whole view for an admin who sees
      // everything (2026-10-21: "shouldn't it be 10 blind").
      $("sight").textContent = `${you.blind ? `${you.blind} blind / ` : ""}${you.dark ?? 1} dark / ${you.lit ?? LIT_SIGHT} light${you.wraith ? " / wraith" : ""}`;
      const foodFrac = Math.max(0, Math.min(1, you.food / this.game.maxFood));
      $("foodbar").style.width = `${foodFrac * 100}%`;
      // Said in the time the meal will really last this character, which
      // is half as long again for a hobbit (2026-09-29). The bar is a
      // share of the stomach and needs nothing of the kind.
      const lasts = you.foodmul ?? 1;
      $("foodtext").textContent = you.food > 0 ? `${mmss(Math.round(you.food * lasts))} of this meal` : "no food";
      $("foodbar").parentElement!.classList.toggle("empty", you.food === 0);
      // The Food row is the whole of it: the meal in the stomach and every
      // meal in the bag, which is how long the mending can go on. Asked
      // for on 2026-09-23: "show a food field in the stats panel, it
      // calculates how much food time the player has remaining." The bag
      // eats for you now, so the bar above is only ever the meal you are
      // on; this is the number that says whether to go and find more.
      $("foodtotal").textContent = hmmss(foodTotal);
      this.showStatuses(you);
      this.showSkills(you.skills, you.debug);
      // The server has the last word on a logout; greying the button just
      // saves the player from being refused to find out why.
      this.logoutBtn.disabled = !you.canlogout;
      // The hover is the server's sentence, so it says what the rule says
      // and how long is left of it rather than a guess at either.
      this.logoutBtn.title = you.canlogout
        ? "Leave the game"
        : you.logoutwhy ?? "You may not log out yet";
      // The stance (2026-10-08: "make our pvp stances white, yellow or
      // red"): the button wears the stance's hand in the stance's colour
      // and says what a press changes it to, and turns red while it is
      // the one that can earn a skull. Being attackable does not depend
      // on it: whoever attacks you can be fought back in any stance. The
      // Stance row in the stats says the same.
      this.pvpBtn.querySelector(".word")!.textContent = stanceWord(you.stance);
      this.pvpBtn.querySelector("svg")?.replaceWith(viewGlyph(stanceGlyph(you.stance)));
      for (const st of STANCES) {
        this.pvpBtn.classList.toggle(`stance-${st}`, st === you.stance);
        $("stance").classList.toggle(`stance-${st}`, st === you.stance);
      }
      this.pvpBtn.classList.toggle("on", you.stance === "red");
      this.pvpBtn.title = stanceTitle(you.stance);
      $("stance").textContent = you.stance;
      // The auto stance reads the same way as the PvP one (2026-10-08: "add
      // auto stances: none, aware, all"): the word, the hover saying what
      // a press changes it to, and the Auto row in the stats. All is the
      // ordinary state, so anything else wears the colour, as off did.
      this.autoBtn.querySelector(".word")!.textContent = autoWord(you.autostance);
      for (const st of AUTO_STANCES) {
        this.autoBtn.classList.toggle(`auto-${st}`, st === you.autostance);
        $("autostance").classList.toggle(`auto-${st}`, st === you.autostance);
      }
      this.autoBtn.classList.toggle("on", you.autostance !== "all");
      this.autoBtn.title = autoTitle(you.autostance);
      $("autostance").textContent = you.autostance;
      // Chasing is the ordinary state, so off is the one worth marking —
      // the same way round as auto-attack, and for the same reason: what
      // the colour says is "this is not how it usually behaves".
      // Ride, for somebody with a horse: lit while you are on it.
      this.rideBtn.hidden = !you.horse;
      this.rideBtn.setAttribute("aria-label", you.riding ? "Dismount" : "Ride");
      this.rideBtn.setAttribute("aria-pressed", String(!!you.riding));
      this.rideBtn.classList.toggle("on", !!you.riding);
      this.rideReady = you.riding ? 0 : (you.rideready ?? 0);
      // Red while the horse will not have you, as a spell the server would
      // refuse is (2026-11-02: "when you can't ride, show the mount button
      // greyed out", then "maybe show it red ... (like spells?)"): under the ground
      // without a staff up, or a fight's wait. Still pressable, so a press
      // or a hold says why, as the hover does.
      this.rideNo = you.riding ? "" : (you.rideno ?? "");
      this.rideBtn.classList.toggle("cant", !!this.rideNo);
      this.rideBtn.setAttribute("aria-disabled", String(!!this.rideNo));
      this.paintRideWait();
      this.chaseBtn.querySelector(".word")!.textContent = you.chaseon ? "Chase: on" : "Chase: off";
      this.chaseBtn.classList.toggle("on", !you.chaseon);
      this.chaseBtn.title = you.chaseon
        ? "Chase is on: you walk after whatever you pick a fight with. Click to fight from where you stand instead"
        : "Chase is off: what you pick is fought from where you stand, never followed. Click to walk after it again";
      // The switch is a diagnostic, so on is the unusual state and the
      // one that wears the colour.
      // The word only: the drawing beside it is the button's other child
      // (2026-09-30: "Add icons to the new menu buttons like log out").
      this.dbgBtn.querySelector(".word")!.textContent = you.debug ? "Debug: on" : "Debug: off";
      this.dbgBtn.classList.toggle("on", you.debug);
      // The rows of arithmetic on the stats panel — the meal bar, HP
      // regen, Mana regen and Speed — are behind the debug switch
      // at every width. Clank was among them until 2026-10-08 ("don't
      // require debug on to show clank stat").
      // Attack and Defence are not, since 2026-09-23: "show attack and
      // defense without debug enabled", and nor is Armor, which a narrow
      // screen hid until 2026-10-07: "show armor in info panel without
      // requiring debug mode to be on". All that is said here is
      // the fact: the switch is on, or it is not. Which of them each
      // screen does anything about is a question of width, and width is
      // the stylesheet's business. It is safe in here because
      // you.debug is part of the key above, so a flick of the switch is
      // exactly what brings us back through this block.
      document.documentElement.classList.toggle("debugon", you.debug);
      // What the switch is for is the numbers behind things, of which the
      // swings are only the loudest: the rows on the stats panel, the
      // rings that say what can hear you. (The kind at the head of each
      // log line was one of them until 2026-10-08, and is the Types
      // switch's now.) It was called the damage debug when the sums were all
      // it did, and the name outlived that by some way (asked for on 2026-09-19:
      // "Debug toggle isn't just for damage, so fix text relating to
      // that"), so the sentence names the whole of it and leads with the
      // half anybody switches it on for.
      this.dbgBtn.title = you.debug
        ? "Debug is on: every swing you are either end of is logged as the sum behind it, the stats panel carries its arithmetic, and the rings show alertness in blue and clank in red, other players' clank included. Click to stop"
        : "Debug is off. Click for the numbers behind things: the sum behind every swing you are either end of, the arithmetic rows on the stats panel, and rings round every monster and you: blue how far it notices, red how far off it is noticed, and red round every other player";
    }
    // The spells get a key of their own rather than riding in youKey
    // above, and for two reasons. Their refusals have clocks in them —
    // "Wait 7 seconds" — so they change on a beat that has nothing to do
    // with the stat panel's; and they are live buttons with listeners
    // hung on them, so rebuilding them because a hitpoint moved would
    // throw away the one the mouse is over mid-hover. The key is the
    // whole of what the panel draws, the refusal and the moot sentence
    // included, because those two are what decide the colour and the
    // hover alike.
    const spellKey = `${you.spellsnote ?? ""}#` + you.spells.map((s) => `${s.name}/${s.mana}/${s.icon ?? ""}/${s.why ?? ""}/${s.moot ?? ""}/${s.left ?? 0}/${s.ready ?? 0}`).join("|");
    if (spellKey !== this.lastSpellKey) {
      this.lastSpellKey = spellKey;
      this.showSpells(you.spells, you.spellsnote);
    }
    // Greyed when there is nothing to stop, so it doubles as a sign that a
    // target is held and being chased, or a clicked walk is being walked.
    this.stopBtn.disabled = !you.target && !you.walking;
    // What is in use is part of the key: wielding a bow changes the outlines
    // without moving a single item.
    this.showGround();
    // The owner and the colour are in the key with the id and the count: a
    // skull whose owner has just logged in wears a new colour with nothing
    // else about the bag changed, and a key blind to it would leave the old
    // picture in the square.
    const invKey = you.inv.map((i) => `${i.id}/${i.owner ?? ""}/${i.var ?? ""}/${i.color ?? ""}x${i.count}`).join(",") +
      "|" + you.equip.join(",") + "/" + (you.equipvars ?? []).join(",") + "|" + (you.chosen ?? []).join(",") + "|" + (you.maxcn ?? 0) +
      // What is pinned, so a pin or an unpin moves the kind at once.
      "|" + (you.pins ?? []).join(",") +
      // How it is sorted, so switching it from the icon's menu redraws the bag.
      "|" + this.bagSort +
      // What the vendor pays, while his panel is open: the bag carries the
      // prices then, so opening and shutting it redraw the bag. Who he is
      // goes in too, since the tooltips name him (Grubb or, since
      // 2026-09-27, Elrond: "make elrond a vendor").
      "|" + (this.selling() ? `${you.vendorname ?? ""}:` + (you.vendorsell ?? []).map((l) => `${l.id}/${l.owner ?? ""}/${l.var ?? ""}:${l.price}/${l.lot ?? 1}`).join(",") : "");
    if (invKey !== this.lastInvKey) {
      this.lastInvKey = invKey;
      // The bag is cut into squares and put in order here rather than
      // arriving that way: the server sends the piles it holds and what is
      // in use, and the picture is this client's (see bag.ts).
      //
      // A bow or an axe takes both hands, so a shield in the bag is down
      // while one is in use; the tooltip on the shield says why it is not
      // outlined.
      const laid = layOutBag(you.inv, this.game.itemDefs, {
        equip: you.equip,
        equipvars: you.equipvars,
        chosen: you.chosen,
        pins: you.pins,
        goldID: GOLD,
        sort: this.bagSort,
      });
      const { squares, equippedPiles, chosen, pinned, twoHanded } = laid;
      this.bagSquares = squares;
      this.inv.replaceChildren();
      // A square for each thing in the bag and none for the room left.
      // Asked for on 2026-09-29: "Don't show empty tiles in the
      // inventory". It used to draw as many squares as the bag holds and
      // leave the rest empty, which is how the grid said how full the bag
      // was; with those gone the panel says it on hover instead, and an
      // empty bag says so in a quiet line rather than leaving a blank.
      //
      // The squares follow the things, however many there are: a bag over
      // the line (an admin's grant, a character saved on a day the number
      // was different) keeps every square it needs, so nothing in it is
      // ever out of sight or out of reach. The server's own answer to the
      // same case is the same one (see addToInventory).
      // How many kinds of thing, since there is no number of squares to
      // say it of: the bag holds any number of kinds since 2026-10-05
      // ("don't limit the number of different kinds of objects in the
      // inventory"), and its one limit is the weight, on the bag's icon.
      this.inv.title = `${squares.length} ${squares.length === 1 ? "kind" : "kinds"} of thing`;
      // What it weighs is not said here any more. It was a line of numbers
      // over the squares from 2026-10-04 ("reintroduce encumbrance") until
      // 2026-10-07, when it moved onto the bag's icon as a fill, with the
      // numbers on the icon's hover ("show the inventory encumbrance
      // "fill" on the icon and show more detail by hovering", and "don't
      // show the numbers in the panel anymore"): see bagweight.ts, and
      // paintBagLoad in main.ts.
      if (squares.length === 0) {
        const none = document.createElement("span");
        none.className = "none";
        none.textContent = "Nothing in your bag";
        this.inv.appendChild(none);
      }
      for (let s = 0; s < squares.length; s++) {
        const it = squares[s];
        const slot = document.createElement("div");
        // Outlined by what is in it rather than by where it is: what is in
        // use is named by id now, and the square holding it is whichever
        // one this client drew it in.
        const inUse = it !== undefined && !it.owner && equippedPiles.has(`${it.id}|${it.var ?? ""}`);
        slot.className = inUse ? "slot equipped" : "slot";
        if (it) {
          const def = this.game.defOf(it);
          const c = document.createElement("canvas");
          c.width = 32;
          c.height = 32;
          keptContext(c).drawImage(itemSprite(it.id, it.color, this.game.defOf(it)?.make), 0, 0);
          slot.appendChild(c);
          if (it.count > 1) {
            const n = document.createElement("span");
            n.className = "count";
            n.textContent = num(it.count);
            slot.appendChild(n);
          }
          // A pinned kind wears a pin in the corner nothing else uses, so
          // it is plain why it sits at the top (2026-10-17).
          if (pinned.has(it.id)) {
            slot.classList.add("pinned");
            const pin = document.createElement("span");
            pin.className = "pin";
            pin.setAttribute("aria-hidden", "true");
            slot.appendChild(pin);
          }
          // With Grubb's Vendor panel open the bag says what he would pay,
          // in the corner and the colour the Vendor panel gives its own
          // prices: "2" for a thing she buys one at a time, and "1/3" for
          // one gold for three of a thing she takes a few at a time. Asked
          // for on 2026-09-24: "when next to the vendor, show sell prices
          // in yellow in the same way as the buy prices are shown. if more
          // than 1 item is required, show it like 1/5". Only while the
          // panel is open, not merely standing beside him, since
          // 2026-10-08: "the value of items in the inventory should not be
          // shown unless the vendor panel is open". See selling.
          const sale = this.saleOf(it);
          if (sale) {
            const p = document.createElement("span");
            p.className = "sellprice";
            const lot = sale.lot ?? 1;
            p.textContent = lot > 1 ? `${num(sale.price)}/${num(lot)}` : num(sale.price);
            slot.appendChild(p);
            this.wireSellDrag(slot, s, it, sale);
          }
          slot.title = def ? this.slotTitle(def, inUse, twoHanded, it, chosen.has(it.id)) : "?";
          // For the depot screen's filter (main.ts), which names the bag,
          // the depot and the market at once.
          slot.dataset.name = def?.name ?? "";
          if (pinned.has(it.id)) slot.title += " Pinned to the top of the bag.";
          if (sale) {
            const lot = sale.lot ?? 1;
            slot.title += ` ${you.vendorname ?? "The vendor"} pays ${num(sale.price)} gold ${lot > 1 ? `for ${num(lot)}` : "each"}: drag it onto what is for sale to sell.`;
          }
          // The plain tooltip is kept aside, since a trade borrows the
          // slot and writes its own over it; the id is what the trade's
          // marks find the slot by.
          slot.dataset.title = slot.title;
          slot.dataset.id = String(it.id);
          // The owner rides along with the id, since the trade's marks find
          // the square by what is in it and two squares of skulls are told
          // apart by nothing else.
          slot.dataset.owner = it.owner ?? "";
          slot.dataset.var = it.var ?? "";
          slot.dataset.name = def?.name ?? "something";
          // The item id goes along with the slot, because the bag can be
          // reordered by the time the server reads the click: wielding
          // moves a weapon to the front, so a second click on the same
          // slot would otherwise land on whatever slid into it.
          //
          // While a trade is open the bag is the way of putting things on
          // the table, and every click on it is spent on that: a click
          // offers one more of the thing, a right-click or a long press
          // the whole pile. Nothing is used, dropped or stored by mistake
          // in the middle of a trade, and nothing is offered by mistake
          // outside one. The count sent is the whole number the offer
          // should stand at — never more than the bag holds, since an
          // offer of forty rocks from a bag of thirty is a promise the
          // server would only have to cut down.
          slot.onclick = (e) => {
            if (this.swallowClick) return; // the end of a drag, not a click
            // Shift asks what a thing is rather than doing anything with
            // it — the same question a shift-click asks of the ground, and
            // the same answer, so the bag and the floor are read the same
            // way. It comes first, and it holds during a trade too: asking
            // what something is should never be the click that puts it on
            // the table.
            if (e.shiftKey) {
              this.onLook(s, it);
              return;
            }
            if (this.game.trading) this.offer(it, this.game.offered(it) + 1);
            // At the depot the click is what moves things, which is the
            // gesture the whole panel is for: you are there to empty a
            // bag, and a trip to the boxes was twenty-five right-clicks.
            // Asked for on 2026-09-23: "Tapping a slot in the inventory
            // or depot while standing near the depot should transfer one
            // stack worth." Using a thing is still on the hold's menu,
            // since the tap was the only way to eat or wield and a depot
            // is a place people stand for a while.
            else if (this.game.you?.indepot) this.onDeposit(s, it, 0);
            else if (!this.tapBurst.slidIn(s, it.id, it.owner ?? "", performance.now())) this.onUse(s, it);
          };
          // Ctrl+right-click puts the whole pile down: stored at the
          // depot, offered in a trade, dropped anywhere else. It was
          // shift+right-click until 2026-09-24 ("make extra sure that
          // shift+right click is not allowed on some browsers, and if so
          // move it to ctrl+right click"): Firefox shows its own menu for
          // a shift+right-click and never tells the page, so there the
          // pile could not be dropped at all. Only with the real right
          // button, since on a Mac a ctrl+click is how a one-button mouse
          // right-clicks, and that is a plain right-click. See ctrlRight
          // in main.ts.
          const pile = () => {
            if (this.game.trading) this.offer(it, this.game.held(it));
            else if (this.game.you?.indepot) this.onDeposit(s, it, 0);
            else this.onDrop(s, it, 0);
          };
          // A finger has one gesture where a mouse has two, so the long
          // press opens a menu of them rather than picking one: a phone
          // that could only ever drop one of a thing could not put a pile
          // down at all. In a trade the menu has nothing to offer that the
          // two clicks do not, so the press is the pile.
          //
          // A plain right-click is the same press since 2026-09-27 ("make
          // right click do whatever long press does"): it used to drop one,
          // and dropping one is on the menu. See longPress.
          //
          // Away from the depot the press is the quantity dialog itself,
          // at the pile pressed, with Sell beside Drop when Grubb buys it
          // and what the thing is written in it (2026-09-27: "when
          // long-press/right clicking a stack in the inventory, go
          // directly to the dialog with drop/sell. set the count to the
          // count of the stack that was clicked. offer sell/drop if those
          // are valid in current locations, and include the look text
          // always"). It was a menu of Sell… and Drop…, which were the
          // only two things on it.
          longPress(slot, (at) => {
            if (at.ctrlKey && at.button === 2) pile();
            else if (this.game.trading) this.offer(it, this.game.held(it));
            else this.dropFromBag(s, it, this.saleOf(it));
          });
        }
        this.inv.appendChild(slot);
      }
      // The bag changed under an open trade. An offer is a promise about
      // what is in the bag, so one the bag can no longer keep is cut down
      // to what it can — dropping ten of forty offered rocks takes the
      // offer to thirty rather than leaving it to fail at the end. The
      // server would refuse the trade at completion anyway; saying so now,
      // through the ordinary offer message, is what clears the accepts
      // and puts the smaller number in front of both players.
      if (this.game.trading) {
        for (const o of this.game.trade!.mine ?? []) {
          const have = this.game.held(o);
          if (have < o.count) this.onOffer(o, have);
        }
      }
    }
    this.paintOffers();
    this.showDepot(you);
    this.showVendor(you);
    this.showEquip(you);
  }

  /**
   * The Equipment panel: a square for every place something is worn or
   * held, what is held first, then head to foot and what is carried, each
   * with what is there or, when nothing is, the name of the place. Asked for on
   * 2026-10-06: "Add a panel for equipped items, it has slots for what you
   * can wear in different locations and selecting a slot lets you choose
   * from valid items from your inventory". A press on a square is the
   * choosing: main.ts offers what in the bag goes there.
   *
   * The places and what fills them are the server's (You.worn), so the
   * panel draws and never decides: the same rule that picks the plate when
   * nothing is chosen picks it here.
   */
  private showEquip(you: You) {
    const worn = you.worn ?? [];
    // How many of what is in a place the bag holds, every pile of it
    // together as the server counts ammunition, so a hand of spears or a
    // stack of throwing stones says how many are left (2026-10-22: "show
    // item counts on the icons in equipment panel").
    const held = (w: Worn) => (w.id ? you.inv.reduce((n, it) => (!it.owner && sameItem(it, { id: w.id, var: w.var }) ? n + it.count : n), 0) : 0);
    const key = worn.map((w) => `${w.place}:${w.id}.${w.var ?? ""}x${held(w)}:${w.pick}:${w.busy ? 1 : 0}:${w.ammo ?? 0}x${w.ammocount ?? 0}:${w.options.join(".")}`).join("|");
    if (key === this.lastEquipKey) return;
    this.lastEquipKey = key;
    this.equipGrid.replaceChildren(
      ...worn.map((w) => {
        const slot = document.createElement("button");
        slot.type = "button";
        slot.className = `slot wornslot pick-${w.pick}` + (w.busy ? " busy" : "");
        slot.dataset.place = w.place;
        const def = w.id ? this.game.defOf((this.game.you?.inv ?? []).find((it) => sameItem(it, { id: w.id, var: w.var })) ?? { id: w.id }) : undefined;
        // The arrows or bolts a bow or a crossbow shoots stand in the off
        // hand while it is in both (2026-10-08: "when using a 2 handed
        // ranged weapon that requires ammunition, show the ammunition in
        // the off hand"), with how many are left, greyed with none.
        const ammo = w.ammo ? this.game.itemDefs.get(w.ammo) : undefined;
        const left = w.ammocount ?? 0;
        if (w.ammo) {
          slot.classList.add("ammo");
          slot.classList.toggle("empty", left === 0);
          const c = document.createElement("canvas");
          c.width = 32;
          c.height = 32;
          keptContext(c).drawImage(itemSprite(w.ammo), 0, 0);
          slot.appendChild(c);
          const n = document.createElement("span");
          n.className = "count";
          n.textContent = num(left);
          slot.appendChild(n);
        } else if (w.id) {
          const c = document.createElement("canvas");
          c.width = 32;
          c.height = 32;
          keptContext(c).drawImage(itemSprite(w.id, undefined, def?.make), 0, 0);
          slot.appendChild(c);
          const have = held(w);
          if (have > 1) {
            const n = document.createElement("span");
            n.className = "count";
            n.textContent = num(have);
            slot.appendChild(n);
          }
        } else {
          const name = document.createElement("span");
          name.className = "placename";
          name.textContent = w.label;
          slot.appendChild(name);
        }
        const ammoName = ammo ? `${ammo.name}s` : "";
        const how =
          ammo ? (left ? "ready to shoot" : `no ${ammoName} left to shoot`) :
          w.busy ? "both hands are on the weapon" :
          w.pick === "chosen" ? "chosen by you" :
          w.pick === "off" ? (w.auto ? "taken off" : "nothing on") :
          w.id ? "the best in your bag" : "nothing in your bag goes here";
        // What it is reads as it does on its square in the bag, the kind's
        // stats, how many and what they weigh, with where it is and why
        // after them (2026-10-22: "the tooltip on equipment icons should
        // show the same basic stats as when in the bag").
        const facts =
          ammo ? this.slotFacts(ammo, { id: w.ammo!, count: left }) :
          def ? this.slotFacts(def, { id: w.id, var: w.var, count: held(w) }) : [];
        slot.title = `${w.label}: ${[...facts, how].join(" · ")}. Press to choose what goes here; long-press or right-click for what it is and what to do with it`;
        slot.setAttribute("aria-label", slot.title);
        // A press chooses; a hold or a right-click opens what a hold on the
        // thing's square in the bag opens (2026-10-17: "Long pressing an
        // item in the equipment panel should show the same dialog as when
        // you long press in the bag"). It said what was there in a small
        // box beside the square from 2026-10-09 ("long-pressing or right
        // clicking an item in the equipment panel should show item
        // information"), which an empty place still does.
        slot.addEventListener("click", () => this.onEquipSlot(w, slot));
        longPress(slot, (at) => this.pressWorn(w, slot, at));
        return slot;
      }),
    );
  }

  /**
   * A hold or a right-click on an Equipment square: what the same press on
   * the thing's square in the bag does, since what is worn is in the bag
   * (the arrows or bolts, when the off hand shows them), branch for branch
   * the bag's hold in update. A place with nothing in it says why, beside it.
   */
  private pressWorn(w: Worn, slot: HTMLElement, at: PressAt) {
    const id = w.ammo || w.id;
    // The very pile in use, made or numbered as it is, and not the first
    // of its kind in the bag (2026-10-22: "the context menu for a made
    // giant sword in the equipment panel shows the regular variety from
    // the bag, not the specific make that's equipped").
    const v = w.ammo ? "" : (w.var ?? "");
    const sq = this.bagSquares;
    const is = (it: Item) => it.id === id && (it.var ?? "") === v;
    let s = id ? sq.findIndex((it) => is(it) && !it.owner) : -1;
    if (s < 0 && id) s = sq.findIndex(is);
    if (s < 0) {
      this.onEquipInfo(w, slot);
      return;
    }
    const it = sq[s];
    if (at.ctrlKey && at.button === 2) {
      if (this.game.trading) this.offer(it, this.game.held(it));
      else if (this.game.you?.indepot) this.onDeposit(s, it, 0);
      else this.onDrop(s, it, 0);
    } else if (this.game.trading) this.offer(it, this.game.held(it));
    else this.dropFromBag(s, it, this.saleOf(it));
  }

  /**
   * Set how many of an item are on the table, within what the bag holds.
   * Every way of offering goes through here, so nothing is ever asked for
   * that the server would have to clamp; a count that is already what it
   * would be is not sent at all, since the server answers every offer by
   * clearing both accepts, and a click that changed nothing should not.
   */
  private offer(it: Item, count: number) {
    const want = Math.max(0, Math.min(count, this.game.held(it)));
    if (want === this.game.offered(it)) return;
    this.onOffer(it, want);
  }

  /**
   * The marks a trade leaves on the bag: an outline on every square with
   * some of its contents on the table, the number offered in the corner
   * the count does not use, and a tooltip that says what a click does now
   * — which is not what the plain tooltip says. All of it is put back the
   * way it was when the trade ends, from the tooltip kept aside for that.
   * The squares themselves are not rebuilt, since the bag has not changed,
   * only what a click on it means.
   */
  private paintOffers() {
    const trading = this.game.trading;
    for (const slot of Array.from(this.inv.children) as HTMLElement[]) {
      const id = Number(slot.dataset.id);
      if (!id) continue;
      const what = { id, count: 0, owner: slot.dataset.owner || undefined, var: slot.dataset.var || undefined };
      const offered = trading ? this.game.offered(what) : 0;
      slot.classList.toggle("offered", offered > 0);
      slot.querySelector(".offer")?.remove();
      if (!trading) {
        slot.title = slot.dataset.title ?? "";
        continue;
      }
      if (offered > 0) {
        const n = document.createElement("span");
        n.className = "offer";
        n.textContent = `${offered}→`;
        slot.appendChild(n);
      }
      const name = slot.dataset.name;
      const held = this.game.held(what);
      slot.title = (offered > 0 ? `${name}: ${offered} of ${held} on the table` : `${name}`) +
        (offered < held ? " — click to offer one, right-click the lot" : " — all of it is on the table; click your offer to take one back");
    }
  }

  /**
   * The trade window, from the last message about it. Each side is a
   * column of squares drawn the way the bag draws them — the same sprites
   * at the same size, the count in the same corner — so what is being
   * offered reads the same as what is being carried. Your own column is
   * the one that answers to clicks: one back for a click, the lot for a
   * right-click or a long press, mirroring the bag beside it. Theirs is a
   * thing to read.
   *
   * Over each column is the name and whether that side has accepted, as a
   * tick or a wait, and under both is the one line that must never be
   * missed: when an accept that was given is gone. That is the moment the
   * whole design exists for — an offer changed after somebody said yes —
   * so it is written out rather than left to a button that is no longer
   * pressed.
   */
  onTrade(t: Trade, prev: Trade | null) {
    if (t.state !== "open") {
      this.paintOffers();
      return;
    }
    const mine = t.mine ?? [];
    const theirs = t.theirs ?? [];
    $("tradetitle").textContent = `Trading with ${t.with}`;
    this.tradeMineName.textContent = t.iaccept ? "You ✓ accepted" : "Your offer";
    this.tradeMineName.classList.toggle("accepted", t.iaccept);
    this.tradeTheirName.textContent = t.theyaccept ? `${t.with} ✓ accepted` : `${t.with}'s offer`;
    this.tradeTheirName.classList.toggle("accepted", t.theyaccept);
    this.offerColumn(this.tradeMine, mine, true);
    this.offerColumn(this.tradeTheirs, theirs, false);
    // An accept that was given and is now gone. It stays on screen until
    // you accept again, whatever else arrives in between: the point is that
    // it cannot be looked away from.
    // The owner is in the key: swapping Ann's skull on the table for Bob's
    // is a change to the offer like any other, and one the other side has
    // to be asked to accept again.
    const key = (items: Item[]) => items.map((i) => `${i.id}/${i.owner ?? ""}/${i.var ?? ""}x${i.count}`).join(",");
    const wasOpen = prev?.state === "open";
    if (wasOpen && (prev!.iaccept || prev!.theyaccept) && !t.iaccept && !t.theyaccept) {
      const who = key(prev!.mine ?? []) !== key(mine) ? "Your offer" : key(prev!.theirs ?? []) !== key(theirs) ? `${t.with}'s offer` : "An offer";
      const after = prev!.iaccept ? "you accepted" : `${t.with} accepted`;
      this.tradeWarn.textContent = `${who} changed after ${after}, so ${ACCEPT_CLEARED}`;
      this.tradeWarn.hidden = false;
    } else if (t.iaccept || !wasOpen) {
      this.tradeWarn.hidden = true;
    }
    this.tradeAccept.setAttribute("aria-pressed", String(t.iaccept));
    this.tradeAccept.textContent = t.iaccept ? "Accepted ✓" : "Accept";
    this.tradeAccept.title = t.iaccept
      ? "You have accepted both offers as they stand. Any change to either takes it back"
      : "Accept both offers exactly as they stand. Nothing moves until both of you have";
    this.paintOffers();
  }

  /** One column of the trade window: the squares of one side's offer. */
  private offerColumn(el: HTMLElement, items: Item[], mine: boolean) {
    el.replaceChildren();
    for (let s = 0; s < Math.max(OFFER_SQUARES, items.length); s++) {
      const it = items[s];
      const slot = document.createElement("div");
      slot.className = "slot";
      if (it) {
        const def = this.game.defOf(it);
        const c = document.createElement("canvas");
        c.width = 32;
        c.height = 32;
        keptContext(c).drawImage(itemSprite(it.id, it.color, this.game.defOf(it)?.make), 0, 0);
        slot.appendChild(c);
        if (it.count > 1) {
          const n = document.createElement("span");
          n.className = "count";
          n.textContent = num(it.count);
          slot.appendChild(n);
        }
        const name = def?.name ?? "something";
        const what = it.count > 1 ? `${num(it.count)} ${name}s` : name;
        if (mine) {
          // What the bag no longer holds enough of is marked, and the
          // offer is being cut down to it at the same moment (see
          // update); the mark is for the tick in between.
          const held = this.game.held(it);
          slot.classList.toggle("short", held < it.count);
          slot.title = `${what} on the table${held < it.count ? `, and only ${held} in your bag` : ""} — click to take one back, right-click to take the lot`;
          slot.onclick = () => this.offer(it, it.count - 1);
          // The hold and the right-click, one listener (see longPress).
          longPress(slot, () => this.offer(it, 0));
        } else {
          slot.title = `${what}: what ${this.game.trade?.with ?? "they"} would hand over`;
        }
      }
      el.appendChild(slot);
    }
  }

  /**
   * Whether the bag is selling: beside a vendor with their panel open. The
   * bag says what things are worth only then (2026-10-08: "the value of
   * items in the inventory should not be shown unless the vendor panel is
   * open"), and selling goes with it — the price in the corner, the line on
   * the tooltip, the drag onto the panel and Sell in the drop dialog — since
   * a sale is a price and the panel is where it is made. Standing beside
   * the vendor with the panel shut, the bag is just a bag.
   */
  private selling(): boolean {
    return !!this.game.you?.atvendor && !this.vendorPanel.hidden && this.vendorPanel.classList.contains("open");
  }

  /** What the vendor beside you pays for a pile like this one, when their
   *  panel is open and they buy it at all (see selling). */
  private saleOf(it: Item): CounterItem | undefined {
    if (!this.selling()) return undefined;
    return (this.game.you?.vendorsell ?? []).find((l) => sameItem(l, it));
  }

  /**
   * What a long press or a right-click on a stored square asks: how many to
   * take out, in the same quantity dialog the bag uses (2026-09-28: "right
   * clicking items while in the depot should use the same context dialog as
   * sell and drop"). It was a menu of one, half and the lot. The most is
   * the whole of what is stored, a depot square and a bag square being the
   * same size again since 2026-10-04, and it starts there; what the bag
   * cannot lift of it, the server refuses with the numbers.
   */
  private takeFromDepot(slot: number, it: Item, def?: ItemDef) {
    const name = def?.name ?? "something";
    const most = Math.max(1, it.count);
    const look = def ? this.slotFacts(def, { ...it, count: 1 }).slice(1).join(" · ") : "";
    const flavour = def?.look ?? "";
    this.onHowMany({
      verb: "Take",
      title: `${num(it.count)} ${name}${it.count > 1 ? "s" : ""} in depot`,
      who: "",
      name,
      lot: 1,
      price: 0,
      lots: most,
      start: most,
      look,
      flavour,
      what: "",
      says: () => "",
      go: (n) => this.onWithdraw(slot, it, n),
    });
  }

  /**
   * The depot panel: visible only while standing on the depot floor, and
   * listing what is stored there. A click or a right-click on a stored
   * item takes a square's worth out; a long press asks how many.
   */
  private showDepot(you: You) {
    const inDepot = !!you.indepot;
    this.depotPanel.hidden = !inDepot;
    if (!inDepot) {
      this.lastDepotKey = "@";
      return;
    }
    const items = you.depot ?? [];
    const key = items.map((i) => `${i.id}x${i.count}`).join(",");
    if (key === this.lastDepotKey) return;
    this.lastDepotKey = key;
    this.depot.replaceChildren();
    // A square for each kind stored and none for room left, the way the
    // bag draws since 2026-09-29: the depot was twenty squares, drawn
    // whether full or not, until 2026-10-05 ("Do not limit the kinds in the
    // depot"), and a fixed twenty would hide the twenty-first kind.
    if (items.length === 0) {
      const none = document.createElement("span");
      none.className = "none";
      none.textContent = "Nothing stored";
      this.depot.appendChild(none);
    }
    for (let s = 0; s < items.length; s++) {
      const it = items[s];
      const slot = document.createElement("div");
      slot.className = "slot";
      if (it) {
        const def = this.game.defOf(it);
        const c = document.createElement("canvas");
        c.width = 32;
        c.height = 32;
        keptContext(c).drawImage(itemSprite(it.id, it.color, this.game.defOf(it)?.make), 0, 0);
        slot.appendChild(c);
        if (it.count > 1) {
          const n = document.createElement("span");
          n.className = "count";
          n.textContent = num(it.count);
          slot.appendChild(n);
        }
        // A depot square holds a whole kind, however much of it there is,
        // and so does a bag square again since 2026-10-04, so a click takes
        // the lot. What you are deciding at a depot box is what to carry
        // away, and the weight is the question: the tooltip says what the
        // pile weighs, the way the bag's own tooltips do, so a shelf that
        // will not come out in one piece can be seen not to before the
        // click is refused.
        const cn = def?.cn ? def.cn * it.count : 0;
        slot.title = (def?.name ?? "?") +
          (it.count > 1 ? ` (${num(it.count)} of them)` : "") +
          (cn ? ` (${fmtCn(cn)} cn)` : "") +
          (it.count > 1 ? ` — click takes all ${num(it.count)} out; hold for fewer` : " — click to take it out");
        // The click is the transfer here too (2026-09-23), and there is
        // nothing else a stored square could mean: you cannot eat, wield
        // or drop what is on a shelf.
        // Shift asks what it is instead, the same question a shift-click
        // asks of the bag and of the ground (asked for on 2026-09-23:
        // "allow shift clicking stuff in the depot to look at it"). It
        // comes first, so asking about a thing never takes it off the
        // shelf.
        slot.dataset.name = def?.name ?? "";
        slot.dataset.key = `${it.id}:${it.owner ?? ""}:${it.var ?? ""}`;
        // In the depot screen a click picks the square instead, for the
        // bar under the depot that takes it out or sells it (main.ts,
        // 2026-10-17: "implement the depot screen").
        slot.onclick = (e) => {
          if (e.shiftKey) {
            this.onLook(s, it, true);
            return;
          }
          if (this.onDepotPick?.(s, it)) return;
          this.onWithdraw(s, it, 0);
        };
        // The hold and the right-click are the same menu since 2026-09-27
        // ("make right click do whatever long press does"); the
        // right-click used to take the stack out, which the click does
        // and the menu offers.
        longPress(slot, () => this.takeFromDepot(s, it, def));
      }
      this.depot.appendChild(slot);
    }
  }

  /**
   * The Vendor panel: visible only while standing beside Grubb (Wenna's
   * counter until 2026-09-24),
   * the way the Depot panel is only there at the boxes, and listing what
   * she sells, a lot to a slot with its price in the corner. A click buys
   * one lot. Asked for on 2026-09-24: "when standing next to wenda, show a
   * new panel like we do for the depot titled "Vendor". Clicking in the
   * vendor will buy one of that item."
   *
   * A lot the purse cannot reach is dimmed rather than hidden, so the
   * stock does not shuffle about as the gold comes and goes; clicking it
   * anyway gets the server's own word on why not.
   */
  /**
   * How much of a line of the vendor's stock to buy, asked in the quantity
   * dialog: one lot, the other side of the slider, half or all of what can
   * be had, or anything between. All is as many lots as the purse pays
   * for and the bag can lift, whichever is fewer, and never less than
   * one, so a lot the purse cannot reach still gets the server's own word
   * on why not. It was a menu of one, five or ten lots until 2026-09-27.
   */
  private buyAsk(line: CounterItem, def: ItemDef | undefined): HowMany {
    const you = this.game.you;
    const inv = you?.inv ?? [];
    const gold = availableGold(you);
    const cnOf = (id: number) => this.game.itemDefs.get(id)?.cn ?? 0;
    const lots = buyableLots(inv, gold, line, cnOf, { cn: you?.cn ?? 0, maxcn: you?.maxcn ?? 0 });
    const name = def?.name ?? "something";
    const who = you?.vendorname ?? "The vendor";
    return {
      verb: "Buy",
      who,
      name,
      lot: line.count,
      price: line.price,
      lots,
      what:
        line.count > 1
          ? `${who} sells ${num(line.count)} for ${num(line.price)} gold. You have ${num(gold)} gold.`
          : `${who} sells them for ${num(line.price)} gold each. You have ${num(gold)} gold.`,
      go: (n) => this.onBuy(line.id, n),
    };
  }

  private showVendor(you: You) {
    const at = !!you.atvendor;
    this.vendorPanel.hidden = !at;
    if (!at) {
      this.lastVendorKey = "@";
      return;
    }
    const lines: CounterItem[] = you.vendor ?? [];
    const gold = availableGold(you);
    const key = lines.map((l) => `${l.id}x${l.count}@${l.price}`).join(",") + `|${gold}`;
    if (key === this.lastVendorKey) return;
    this.lastVendorKey = key;
    this.vendor.replaceChildren();
    for (const line of lines) {
      const def = this.game.itemDefs.get(line.id);
      const slot = document.createElement("div");
      slot.className = line.price > gold ? "slot dear" : "slot";
      const c = document.createElement("canvas");
      c.width = 32;
      c.height = 32;
      keptContext(c).drawImage(itemSprite(line.id, undefined, this.game.defOf(line)?.make), 0, 0);
      slot.appendChild(c);
      const price = document.createElement("span");
      price.className = "price";
      price.textContent = num(line.price);
      slot.appendChild(price);
      if (line.count > 1) {
        const n = document.createElement("span");
        n.className = "count";
        n.textContent = num(line.count);
        slot.appendChild(n);
      }
      const what = def ? this.slotFacts(def).join(" · ") : "?";
      const lot = line.count > 1 ? `${num(line.count)} for ${num(line.price)} gold` : `${num(line.price)} gold`;
      slot.title = `${what} — ${lot}. Click to buy ${line.count > 1 ? num(line.count) : "one"}, right-click for more, shift-click to look.`;
      // Shift asks what it is, as it does of the bag and the ground, and
      // buys nothing (2026-09-27: "shift+left clicking should do a look on
      // it"). The click used to buy whatever was held with it.
      slot.onclick = (e) => {
        if (e.shiftKey) this.onLookVendor(line.id);
        else this.onBuy(line.id);
      };
      // A right-click, or a long press, asks how many (2026-09-27: "right
      // clicking an item in the vendor should open a buy menu"). There was
      // nothing on either before: the right-click was the browser's own and
      // a hold did nothing at all. It was a menu of one, five or ten lots
      // until later that day, and is the quantity dialog now.
      longPress(slot, () => this.onHowMany(this.buyAsk(line, def)));
      this.vendor.appendChild(slot);
    }
  }

  /**
   * Let a bag slot be dragged onto the shop's goods, which sells it. Asked
   * for on 2026-09-24: "allow dragging items from the inventory to the
   * vendor to sell". A mouse only: a finger's press-and-move on the bag is
   * the long press and the page's own scroll, and the long press's menu
   * carries the same sale (see slotMenu).
   *
   * The drag is pointer events rather than the browser's own drag and
   * drop, which draws its own ghost, cannot be told apart from dragging the
   * picture out of the page, and does nothing at all on a phone. A press
   * that moves less than a few pixels is still a click.
   */
  private wireSellDrag(slot: HTMLElement, s: number, it: Item, sale: CounterItem) {
    slot.addEventListener("pointerdown", (ev) => {
      if (ev.pointerType !== "mouse" || ev.button !== 0 || ev.shiftKey) return;
      const x0 = ev.clientX,
        y0 = ev.clientY;
      let ghost: HTMLCanvasElement | null = null;
      const move = (e: PointerEvent) => {
        if (!ghost) {
          if (Math.hypot(e.clientX - x0, e.clientY - y0) <= 4) return;
          ghost = document.createElement("canvas");
          ghost.width = ghost.height = 32;
          ghost.className = "dragghost";
          keptContext(ghost).drawImage(itemSprite(it.id, it.color, this.game.defOf(it)?.make), 0, 0);
          document.body.appendChild(ghost);
          this.sellDrop.classList.add("droptarget");
        }
        ghost.style.left = `${e.clientX - 16}px`;
        ghost.style.top = `${e.clientY - 16}px`;
      };
      const up = (e: PointerEvent) => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        if (!ghost) return; // it never moved: a click, and the click handles it
        ghost.remove();
        this.sellDrop.classList.remove("droptarget");
        this.swallowClick = true;
        setTimeout(() => (this.swallowClick = false), 0);
        const over = document.elementFromPoint(e.clientX, e.clientY);
        if (over && this.sellDrop.contains(over)) this.sellFromBag(s, it, sale);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
    });
  }

  /** How many of a thing the bag holds, over every square it fills. */
  private heldOfKind(it: Item): number {
    const inv = this.game.you?.inv ?? [];
    return inv.reduce((n, o) => (o && sameItem(o, it) ? n + o.count : n), 0);
  }

  /** How the bag is sorted now: what the bag icon's menu ticks. */
  get bagSortedBy(): BagSort {
    return this.bagSort;
  }

  /**
   * Sort the bag by kind or by weight, from the bag icon's menu (2026-10-08:
   * "the bag sort by weight should be a context option on the bag panelicon
   * itself, not on the items in the bag."; it was a button in each item's
   * dialog from earlier that day). The bag is drawn again at once in the new
   * order. The choice is written down for the next visit; a browser that
   * will not keep it keeps it for this one.
   */
  sortBag(by: BagSort) {
    this.bagSort = by;
    try {
      localStorage.setItem("tinytibia.bagsort", by);
    } catch {
      /* storage unavailable: the order lasts this session */
    }
    const you = this.game.you;
    if (you) this.update(you);
  }

  /**
   * Put some of a pile down, asking how many in the quantity dialog; and
   * beside a vendor who buys it, the same dialog sells the number picked
   * as well, with Sell and Drop side by side (2026-09-27: "use the sell
   * dialog for drop too. when both actions are available, make it so you
   * can select how many then select drop or sell").
   *
   * The slider counts things rather than the vendor's lots, since a drop
   * is of things. A sale takes as many whole lots as the number holds, and
   * the line under the slider says so when that leaves some over.
   */
  dropFromBag(slot: number, it: Item, sale?: CounterItem) {
    const def = this.game.defOf(it);
    const name = def?.name ?? "something";
    const total = this.heldOfKind(it);
    // The plural the server's Phrase makes (pluralName in items.go): an s,
    // but nothing on a name that ends in one ("3 athelas") and es on one
    // that ends in ss or ch ("2 Umbar cutlasses", "2 torches").
    const pl = (s: string): string => (s.endsWith("ss") || s.endsWith("ch") ? s + "es" : s.endsWith("s") ? s : s + "s");
    // "X of Y" makes X the plural: "2 rings of lore", as the server says it.
    const of = name.indexOf(" of ");
    const plural = (n: number) => `${n} ${n > 1 ? (of > 0 ? pl(name.slice(0, of)) + name.slice(of) : pl(name)) : name}`;
    const drop = (n: number) => this.onDrop(slot, it, n);
    // It starts at the pile that was pressed, and says what the thing is
    // the way a look at it would (2026-09-27: "set the count to the count
    // of the stack that was clicked ... include the look text always").
    const start = Math.max(1, Math.min(it.count, total));
    const look = def ? this.slotFacts(def, { ...it, count: 1 }).slice(1).join(" · ") : "";
    const flavour = def?.look ?? "";
    // The heading is how many the bag holds, and so nothing under it says
    // so again: not the line under the heading, not the line under the
    // slider, which the number box beside the slider already speaks for
    // (2026-09-27: "the dialog for items should be titled like "35 arrows
    // in inventory", and deduplicate that information in the rest of the
    // dialog").
    const title = `${plural(total)} in inventory`;
    // The One Ring tells where the Seven and the Nine are, when it is
    // looked at; no other ring feels anything (2026-10-17: "only the One
    // Ring can feel the other rings"). A shift-click is that look; a
    // finger has no shift, so the dialog a hold opens carries it as a
    // button.
    const last: NonNullable<HowMany["more"]> = def?.feels ? [{ verb: "Feel the others", go: () => this.onLook(slot, it) }] : [];
    // An admin can be rid of a thing outright, the One Ring included, which
    // is lost in the Gladden again rather than unmade (2026-10-08: "admins
    // should have an option to destroy items").
    // Pin or Unpin the kind, so it sorts to the top of the bag (2026-10-17:
    // "pinning is enabled in the item context menu like drop/sell etc.").
    // It is on the kind, not the pile, so the count is not read.
    const pinnedNow = (this.game.you?.pins ?? []).includes(it.id);
    last.unshift({ verb: pinnedNow ? "Unpin" : "Pin", go: () => this.onPin(it, !pinnedNow) });
    // Put it on, or take it off, in the place the Equipment panel would
    // (2026-10-17: "Show equip, and unequip as options"): the same equip
    // its menu sends. A skull with a name on it goes nowhere (see fitsHere).
    const worn = this.game.you?.worn ?? [];
    const on = worn.find((w) => w.id === it.id && (w.var ?? "") === (it.var ?? "") && !w.ammo);
    const fits = it.owner ? undefined : worn.find((w) => w.options.includes(it.id) && !w.ammo);
    if (on) last.unshift({ verb: "Unequip", go: () => this.onEquip(on.place, -1) });
    else if (fits) last.unshift({ verb: "Equip", go: () => this.onEquip(fits.place, it.id, it.var) });
    // The One is cast into the Fire from here, and only from here (2026-10-19:
    // "The One should have an entry in the context menu "Cast it into the
    // Fire""; since 2026-10-22 the click on it at the Cracks only puts it on);
    // away from the Cracks the server only says where the Fire is.
    if (def?.cast) last.unshift({ verb: "Cast it into the Fire", heavy: true, go: () => this.onCastOne(it) });
    if (this.game.you?.admin) last.push({ verb: "Destroy", heavy: true, go: (n) => this.onDestroy(slot, it, n) });
    // At the depot the dialog stores first, and drops, sells and uses
    // besides (2026-09-28: "right clicking items while in the depot should
    // use the same context dialog as sell and drop"). Use is here because
    // the tap that used to use a thing is the transfer at the boxes, and
    // this is where the menu it had gone to used to be.
    if (this.game.you?.indepot) {
      const lot = sale?.lot ?? 1;
      const more: NonNullable<HowMany["more"]> = [];
      if (sale) more.push({ verb: "Sell", detail: (n) => sellDetail(n, lot, sale.price), go: (n) => { if (n >= lot) this.onSell(slot, it, Math.floor(n / lot)); } });
      // Using a piece of gear only puts it on or takes it off, which the
      // Equip or Unequip after it already says, so it is not offered as Use
      // as well (2026-10-19: "instead of use, say "Equip" if that's what
      // would happen").
      if (!on && !fits) more.push({ verb: "Use", go: () => this.onUse(slot, it) });
      more.push(...last);
      this.onHowMany({
        verb: "Store",
        title,
        who: "",
        name,
        lot: 1,
        price: 0,
        lots: total,
        start,
        look,
        flavour,
        what: "",
        says: () => "",
        go: (n) => this.onDeposit(slot, it, n),
        also: { verb: "Drop", go: drop },
        more,
      });
      return;
    }
    if (!sale) {
      this.onHowMany({
        verb: "Drop",
        title,
        who: "",
        name,
        lot: 1,
        price: 0,
        lots: total,
        start,
        look,
        flavour,
        what: "",
        says: () => "",
        go: drop,
        more: last,
      });
      return;
    }
    const lot = sale.lot ?? 1;
    const who = this.game.you?.vendorname ?? "The vendor";
    this.onHowMany({
      verb: "Sell",
      title,
      who,
      name,
      lot: 1,
      price: 0,
      lots: total,
      start,
      look,
      flavour,
      what: "",
      // What it fetches is on the button; the line under the slider says
      // only what the button cannot.
      detail: (n) => sellDetail(n, lot, sale.price),
      says: (n) => {
        const sold = Math.floor(n / lot);
        if (sold < 1) return "Too few to sell";
        const over = n - sold * lot;
        return over ? `${num(over)} left over` : "";
      },
      can: (n) => n >= lot,
      go: (n) => {
        if (n >= lot) this.onSell(slot, it, Math.floor(n / lot));
      },
      also: { verb: "Drop", go: drop },
      more: last,
    });
  }

  /**
   * Sell a pile from the bag: straight away when there is only one lot of
   * it to sell, and otherwise by asking how many — "if multiple items could
   * be sold, show a dialog to select how many to sell" (2026-09-24). What
   * could be sold is every one of the kind in the bag, not only the pile
   * dragged, since that is what the server sells out of.
   */
  sellFromBag(slot: number, it: Item, sale: CounterItem) {
    const lot = sale.lot ?? 1;
    const total = this.heldOfKind(it);
    const lots = Math.floor(total / lot);
    if (lots <= 1) {
      // One lot, or not even that: sold, or refused with the server's own
      // word on how many she takes.
      this.onSell(slot, it, 1);
      return;
    }
    const def = this.game.defOf(it);
    const who = this.game.you?.vendorname ?? "The vendor";
    const name = def?.name ?? "something";
    this.onHowMany({
      verb: "Sell",
      // Headed like the press's dialog (see dropFromBag), and for the same
      // reason nothing under it counts the bag again.
      title: `${total} ${name}${total > 1 ? "s" : ""} in inventory`,
      who,
      name,
      lot,
      price: sale.price,
      lots,
      what: "",
      detail: (n) => sellDetail(n * lot, lot, sale.price),
      says: () => "",
      go: (n) => this.onSell(slot, it, n),
    });
  }

}
